<template>
  <div class="workspace-container">
    <div class="workspace-layout" v-if="!hasModules || wizardActive">
      <!-- ========== 状态 A：首次进入向导 ========== -->
      <div class="workspace-main">
        <!-- 步骤指引 -->
        <div class="card step-card">
          <el-steps :active="currentStep" finish-status="success" align-center>
            <el-step title="选择或创建项目"></el-step>
            <el-step title="配置脚手架"></el-step>
            <el-step title="选择本地路径"></el-step>
            <el-step title="扫描 pom.xml"></el-step>
            <el-step title="确认录入模块"></el-step>
            <el-step title="源码扫描标注"></el-step>
          </el-steps>
        </div>

        <!-- 步骤 0：选择或创建项目 -->
        <div class="card" v-if="currentStep === 0">
          <div class="card-header">
            <h3><i class="el-icon-s-home"></i> 选择或创建项目</h3>
          </div>
          <div class="card-body">
            <!-- 模式切换标签 -->
            <el-tabs v-model="createMode" style="margin-bottom:16px;">
              <el-tab-pane label="选择既有项目" name="existing">
                <p class="card-desc">选择要关联的项目，后续扫描到的模块将录入到该项目下。如果没有合适的项目，请先新建。</p>
                <div class="project-select-row">
                  <el-select
                      v-model="queryParams.projectId"
                      @change="projectChange"
                      placeholder="请选择项目"
                      class="project-select"
                      size="small"
                      clearable
                      filterable
                  >
                    <el-option v-for="item in projectOptions" :key="item.id" :label="item.name" :value="item.id"></el-option>
                  </el-select>
                  <el-button type="primary" icon="el-icon-plus" @click="clickAddProject">新建项目</el-button>
                </div>
              </el-tab-pane>
              <el-tab-pane label="从脚手架创建" name="fromScaffold">
                <p class="card-desc">选择一个脚手架，系统将下载脚手架骨架并引导您完成配置，快速启动新项目。</p>
                <div class="base-select-row" style="display:flex;gap:12px;align-items:flex-start;flex-wrap:wrap;">
                  <el-select
                      v-model="selectedScaffoldId"
                      placeholder="请选择脚手架"
                      class="project-select"
                      size="small"
                      clearable
                      filterable
                      style="width:280px;"
                  >
                    <el-option v-for="item in scaffoldOptions" :key="item.id" :label="item.name + ' v' + item.version" :value="item.id">
                      <span style="float:left;">{{ item.name }}</span>
                      <span style="float:right;color:#909399;font-size:12px;">v{{ item.version }}</span>
                    </el-option>
                  </el-select>
                  <el-input
                      v-model="newProjectName"
                      placeholder="输入新项目名称"
                      size="small"
                      style="width:220px;"
                      clearable
                  ></el-input>
                  <el-button type="success" icon="el-icon-check" size="small" @click="createProjectFromScaffold"
                      :disabled="!selectedScaffoldId || !newProjectName" :loading="creatingFromScaffold">
                    创建项目
                  </el-button>
                </div>
                <div v-if="scaffoldOptions.length === 0" style="margin-top:12px;padding:16px;background:#fafafa;border-radius:4px;text-align:center;">
                  <i class="el-icon-info" style="color:#909399;"></i>
                  <span style="color:#909399;font-size:13px;"> 暂无可用脚手架，请先进入「脚手架管理」页面添加脚手架。</span>
                </div>
              </el-tab-pane>
            </el-tabs>
          </div>
          <div class="card-footer">
            <el-button v-if="wizardFromModules" type="text" @click="returnToModules" style="margin-right:auto;color:#909399;">
              <i class="el-icon-close"></i> 取消
            </el-button>
            <el-button type="primary" @click="goToStep(1)" :disabled="!queryParams.projectId">
              下一步 <i class="el-icon-arrow-right"></i>
            </el-button>
          </div>
        </div>

        <!-- 步骤 1：配置脚手架 -->
        <div class="card" v-if="currentStep === 1">
          <div class="card-header">
            <h3><i class="el-icon-setting"></i> 配置脚手架</h3>
          </div>
          <div class="card-body">
            <config-schema-form
              :schema="projectScaffoldSchema"
              v-model="projectConfigValues"
            />
          </div>
          <div class="card-footer">
            <el-button v-if="wizardFromModules" type="text" @click="returnToModules" style="margin-right:auto;color:#909399;">
              <i class="el-icon-close"></i> 取消
            </el-button>
            <el-button @click="goToStep(0)">
              <i class="el-icon-arrow-left"></i> 上一步
            </el-button>
            <el-button type="primary" @click="confirmConfig" :loading="savingConfig">
              {{ projectScaffoldSchema ? '下一步' : '跳过' }} <i class="el-icon-arrow-right"></i>
            </el-button>
          </div>
        </div>

        <!-- 步骤 2：选择本地项目路径 -->
        <div class="card" v-if="currentStep === 2">
          <div class="card-header">
            <h3><i class="el-icon-folder-opened"></i> 选择本地项目路径</h3>
          </div>
          <div class="card-body">
            <div class="info-banner">
              <i class="el-icon-info"></i>
              <span>选择本地项目根目录，系统将自动检测项目类型。
              <strong>Maven/Gradle 项目</strong>自动扫描 pom.xml 识别模块；
              <strong>其他项目</strong>使用 AI 分析目录结构识别模块。</span>
            </div>

            <div class="path-row">
              <el-input
                  class="path-input"
                  v-model="selectedPath"
                  placeholder="选择本地项目根目录"
                  readonly
                  size="small"
              >
                <template v-slot:prepend>
                  <i class="el-icon-folder-opened"></i>
                </template>
              </el-input>
              <el-button type="primary" @click="selectDirectory">
                <i class="el-icon-folder-add"></i> 选择目录
              </el-button>
            </div>

            <div class="tip-text">
              <i class="el-icon-info"></i>
              支持 Chrome 86+ / Edge 86+ 的 File System Access API，自动检测项目类型并分析模块
            </div>

            <!-- 扫描中 -->
            <div v-if="scanning" class="scanning-state">
              <i class="el-icon-loading scanning-spin"></i>
              <p>正在扫描 pom.xml，请稍候…</p>
            </div>
          </div>
          <div class="card-footer">
            <el-button v-if="wizardFromModules" type="text" @click="returnToModules" style="margin-right:auto;color:#909399;">
              <i class="el-icon-close"></i> 取消
            </el-button>
            <el-button @click="goToStep(1)">
              <i class="el-icon-arrow-left"></i> 上一步
            </el-button>
            <el-button type="primary" @click="goToStep(3)" :disabled="!selectedPath">
              下一步 <i class="el-icon-arrow-right"></i>
            </el-button>
          </div>
        </div>

        <!-- 步骤 3：项目模块扫描（AI 驱动） -->
        <div class="card" v-if="currentStep === 3">
          <div class="card-header">
            <h3><i class="el-icon-discover"></i> 项目模块分析</h3>
            <el-tag v-if="projectTypeKey" size="mini" type="success">{{ projectTypeLabel }}</el-tag>
          </div>
          <div class="card-body">
            <div v-if="projectTypeKey === 'MAVEN' || projectTypeKey === 'GRADLE'" class="info-banner">
              <i class="el-icon-info"></i>
              <span>检测到 <strong>{{ projectTypeLabel }}</strong> 项目。系统已扫描以下模块。<strong>三种颜色</strong>分别代表：<em style="color:#409EFF;">新增模块</em>（可勾选录入）、<em style="color:#67C23A;">已录入</em>（本地存在且已在项目中）、<em style="color:#C0C4CC;">仅存在于项目</em>（本地未找到对应文件）。</span>
            </div>
            <div v-else class="info-banner">
              <i class="el-icon-info"></i>
              <span>检测到 <strong>{{ projectTypeLabel }}</strong> 项目。以下是通过 AI 分析目录结构识别的项目模块，可勾选录入系统。
              <el-tag size="mini" type="warning" style="margin-left:8px;">AI 分析</el-tag>
              </span>
            </div>

            <!-- 空状态 -->
            <div v-if="combinedModules.length === 0 && !scanning" class="empty-state" style="padding:30px;">
              <i class="el-icon-folder-opened empty-icon"></i>
              <p v-if="projectTypeKey === 'MAVEN' || projectTypeKey === 'GRADLE'">未扫描到任何模块</p>
              <p v-else>{{ projectTypeLabel || '该项目' }} 未找到 Maven 模块结构</p>
              <div style="display:flex;gap:8px;justify-content:center;margin-top:8px;flex-wrap:wrap;">
                <el-button type="primary" size="small" @click="rescanDirectory" :loading="scanning" :icon="(projectTypeKey === 'MAVEN' || projectTypeKey === 'GRADLE') ? 'el-icon-refresh' : ''">
                  {{ (projectTypeKey === 'MAVEN' || projectTypeKey === 'GRADLE') ? '重新扫描' : '重新选择目录' }}
                </el-button>
                <el-button v-if="projectTypeKey !== 'MAVEN' && projectTypeKey !== 'GRADLE'" type="warning" size="small" icon="el-icon-magic-stick" @click="analyzeModulesByAI" :loading="aiModuleAnalyzing">
                  AI 分析模块结构
                </el-button>
              </div>
            </div>

            <div v-if="combinedModules.length > 0">
              <div class="scan-result-header">
                <h4>模块扫描结果</h4>
                <span>共 {{ combinedModules.length }} 个模块（{{ newModules.length }} 个可录入）</span>
              </div>

              <div class="module-list">
                <div
                    v-for="(item, index) in combinedModules"
                    :key="index"
                    :class="['module-item', getScanItemClass(item)]"
                    @click="toggleScanItem(item)"
                >
                  <div :class="['module-checkbox', { 'module-checkbox-checked': item.checked, 'module-checkbox-disabled': !item.checkable }]">
                    <i v-if="item.checked" class="el-icon-check"></i>
                  </div>
                  <div class="module-info">
                    <div class="module-item-name">
                      {{ item.name }}
                      <span v-if="item.status === 'orphan'" class="missing-badge">本地未找到</span>
                    </div>
                    <div class="module-item-path" v-if="item.status !== 'orphan'">{{ item.name }}/pom.xml</div>
                    <div class="module-item-path" v-else style="color:#C0C4CC;">无本地文件</div>
                  </div>
                  <span :class="['module-tag', getScanStatusTag(item)]">{{ getScanStatusLabel(item) }}</span>
                </div>
              </div>

              <!-- 选中操作栏 -->
              <div class="check-bar">
                <el-checkbox v-model="checkAll" @change="toggleCheckAll" :disabled="newModules.length === 0">全选（仅新增）</el-checkbox>
                <span class="check-count">已选中 {{ checkedModuleCount }} 个新增模块</span>
                <div class="check-actions">
                  <el-button size="small" @click="resetScan">取消</el-button>
                  <el-button type="success" size="small" @click="goToStep(4)" :disabled="checkedModuleCount === 0">
                    <i class="el-icon-arrow-right"></i> 下一步
                  </el-button>
                </div>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <el-button v-if="wizardFromModules" type="text" @click="returnToModules" style="margin-right:auto;color:#909399;">
              <i class="el-icon-close"></i> 取消
            </el-button>
            <el-button @click="goToStep(2)">
              <i class="el-icon-arrow-left"></i> 上一步
            </el-button>
          </div>
        </div>

        <!-- 步骤 4：确认录入模块 -->
        <div class="card" v-if="currentStep === 4">
          <div class="card-header">
            <h3><i class="el-icon-finished"></i> 确认录入模块</h3>
          </div>
          <div class="card-body">
            <p class="card-desc">以下为本次将要录入的新模块，点击「录入」后将保存到数据库。</p>
            <el-table :data="newModules.filter(m => m.checked).map(n => ({ name: n.name, code: n.name }))"
                      style="width: 100%; margin-top: 15px;" border stripe>
              <el-table-column label="序号" type="index" width="60" align="center"></el-table-column>
              <el-table-column label="模块名" prop="name"></el-table-column>
              <el-table-column label="模块编码" prop="code"></el-table-column>
              <el-table-column label="模块类型" width="100" align="center">
                <template v-slot="scope">
                  <span :class="['module-tag', getModuleTagClassStr(scope.row.name)]">{{ getModuleTypeStr(scope.row.name) }}</span>
                </template>
              </el-table-column>
            </el-table>
            <div v-if="existingMatchModules.length > 0" style="margin-top:16px;">
              <p style="font-size:13px;color:#909399;">
                <i class="el-icon-info"></i> 另有 {{ existingMatchModules.length }} 个模块已在项目中，无需重复录入
              </p>
            </div>
            <div v-if="orphanModules.length > 0" style="margin-top:8px;">
              <p style="font-size:13px;color:#C0C4CC;">
                <i class="el-icon-warning-outline"></i> {{ orphanModules.length }} 个模块仅存在于数据库，本地未找到对应文件
              </p>
            </div>
          </div>
          <div class="card-footer">
            <el-button v-if="wizardFromModules" type="text" @click="returnToModules" style="margin-right:auto;color:#909399;">
              <i class="el-icon-close"></i> 取消
            </el-button>
            <el-button @click="goToStep(3)">
              <i class="el-icon-arrow-left"></i> 上一步
            </el-button>
            <el-button type="success" @click="submitModules" :loading="submitting" :disabled="checkedModuleCount === 0">
              <i class="el-icon-upload2"></i> 录入
            </el-button>
          </div>
        </div>

        <!-- 步骤 5：源码 AI 扫描标注 -->
        <div class="card" v-if="currentStep === 5">
          <div class="card-header">
            <h3><i class="el-icon-document-copy"></i> 源码 AI 扫描标注</h3>
            <el-tag v-if="projectTypeKey" size="mini" type="success" style="margin-right:6px;">{{ projectTypeLabel }}</el-tag>
            <el-tag v-if="modelScanDone && !javaScanning" size="small" type="success">已完成</el-tag>
            <el-tag v-else-if="javaScanning" size="small" type="warning">扫描中</el-tag>
          </div>
          <div class="card-body">
            <!-- 扫描模式 -->
            <div style="display:flex;align-items:center;gap:12px;margin-bottom:16px;flex-wrap:wrap;">
              <span style="font-size:13px;color:#909399;">扫描模式：</span>
              <el-select v-model="scanMode" size="mini" style="width:120px;" :disabled="javaScanning">
                <el-option label="预制规则+AI" value="auto"></el-option>
                <el-option label="仅收集（人工）" value="manual"></el-option>
              </el-select>
              <el-button v-if="modelScanDone" size="small" type="success" plain @click="rescanJavaSources" :loading="javaScanning">
                <i class="el-icon-refresh"></i> 重新扫描
              </el-button>
              <el-button v-if="modelScanDone" size="small" @click="openScanSettings">
                <i class="el-icon-setting"></i> 扫描设置
              </el-button>
              <el-button v-if="modelScanDone" size="small" @click="openScanLabelDialog">
                <i class="el-icon-edit-outline"></i> 人工标注
              </el-button>
            </div>

            <!-- 扫描中 -->
            <div v-if="javaScanning" class="scanning-state" style="padding:20px;">
              <i class="el-icon-loading scanning-spin"></i>
              <p>正在扫描 {{ projectTypeLabel || '项目' }} 源码，请稍候…</p>
              <p style="color:#909399;font-size:12px;" v-if="scanProgressText">{{ scanProgressText }}</p>
            </div>

            <!-- 扫描结果 -->
            <div v-else-if="modelScanDone" style="padding:0;">
              <div style="display:flex;gap:16px;flex-wrap:wrap;">
                <div class="stat-item" style="min-width:60px;flex:0 0 auto;">
                  <div class="stat-num" style="font-size:16px;">{{ scanSummary.total }}</div>
                  <div class="stat-label">总文件</div>
                </div>
                <div class="stat-item" style="min-width:60px;flex:0 0 auto;">
                  <div class="stat-num" style="font-size:16px;color:#E6A23C;">{{ scanSummary.enums }}</div>
                  <div class="stat-label">枚举</div>
                </div>
                <div class="stat-item" style="min-width:60px;flex:0 0 auto;">
                  <div class="stat-num" style="font-size:16px;color:#409EFF;">{{ scanSummary.entities }}</div>
                  <div class="stat-label">实体</div>
                </div>
                <div class="stat-item" style="min-width:60px;flex:0 0 auto;">
                  <div class="stat-num" style="font-size:16px;color:#67C23A;">{{ scanSummary.controllers }}</div>
                  <div class="stat-label">控制器</div>
                </div>
                <div class="stat-item" style="min-width:60px;flex:0 0 auto;">
                  <div class="stat-num" style="font-size:16px;color:#909399;">{{ scanSummary.services }}</div>
                  <div class="stat-label">服务</div>
                </div>
                <div class="stat-item" style="min-width:60px;flex:0 0 auto;">
                  <div class="stat-num" style="font-size:16px;color:#C0C4CC;">{{ scanSummary.others }}</div>
                  <div class="stat-label">其他</div>
                </div>
              </div>
            </div>

            <!-- 等待扫描 -->
            <div v-else style="padding:30px;text-align:center;">
              <p style="color:#909399;margin-bottom:16px;">模块已录入，点击下方按钮开始源码扫描</p>
              <el-button type="primary" size="medium" @click="startJavaScan" :loading="javaScanning">
                <i class="el-icon-document-copy"></i> 开始扫描
              </el-button>
            </div>
          </div>
          <div class="card-footer">
            <el-button v-if="wizardFromModules" type="text" @click="returnToModules" style="margin-right:auto;color:#909399;">
              <i class="el-icon-close"></i> 取消
            </el-button>
            <el-button @click="goToStep(4)">
              <i class="el-icon-arrow-left"></i> 上一步
            </el-button>
            <el-button type="primary" @click="finishScan" :disabled="javaScanning">
              {{ modelScanDone || javaModels.length > 0 ? '完成' : '跳过' }} <i class="el-icon-arrow-right"></i>
            </el-button>
          </div>
        </div>
      </div>

      <!-- 右侧功能栏 -->
      <div class="workspace-sidebar">
        <div class="sidebar-card">
          <h4><i class="el-icon-magic-stick"></i> 智慧功能</h4>
          <div class="action-btn-list">
            <AICreateTableButton class="action-btn" style="margin:0;">智能建表</AICreateTableButton>
            <AICreateCodeButton class="action-btn" style="margin:0;">智能生成代码</AICreateCodeButton>
            <el-button class="action-btn" type="warning" plain icon="el-icon-open">便捷开发</el-button>
            <el-button class="action-btn" type="success" plain icon="el-icon-user">人脸SDK集成项目下载</el-button>
            <el-button class="action-btn" type="primary" plain icon="el-icon-document">查看模块详情</el-button>
          </div>
        </div>
        <div class="sidebar-card">
          <h4><i class="el-icon-notebook-2"></i> 使用说明</h4>
          <div class="usage-guide">
            <p><span class="step-num">1</span> 选择或新建一个项目</p>
            <p><span class="step-num">2</span> 选择本地项目根目录（自动检测类型）</p>
            <p><span class="step-num">3</span> AI 分析项目结构，识别模块</p>
            <p><span class="step-num">4</span> 勾选需要录入的模块并确认</p>
            <p><span class="step-num">5</span> AI 扫描源码，自动分类标注</p>
            <p class="usage-note">支持 Java / Node.js / Python / Go 等多类型项目</p>
          </div>
        </div>
        <div class="sidebar-card ai-assistant-card">
          <ai-assistant-panel
            :current-step="currentStep"
            :project-type-label="projectTypeLabel"
            :project-context="{ projectName: currentProjectName, moduleCount: modules.length, scannedFileCount: modelScanCount }"
            @action="handleAIAction"
          />
        </div>
      </div>
    </div>

    <div class="workspace-layout" v-else>
      <!-- ========== 状态 B：已有项目，展示模块 ========== -->
      <div class="workspace-main">
        <!-- 项目信息条 -->
        <div class="project-info-bar">
          <div class="project-info-content">
            <div class="project-info-name">
              <i class="el-icon-office-building"></i> {{ currentProjectName }}
              <el-tag v-if="projectTypeKey" size="mini" type="success" style="margin-left:8px;vertical-align:middle;">{{ projectTypeLabel }}</el-tag>
            </div>
            <div class="project-info-meta">
              <span><i class="el-icon-files"></i> {{ modules.length }} 个模块</span>
              <span v-if="currentProjectPath"><i class="el-icon-folder-opened"></i> {{ currentProjectPath }}</span>
            </div>
          </div>
          <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">
            <el-button size="small" type="primary" plain @click="openBatchConfig">
              <i class="el-icon-setting"></i> 统一配置
            </el-button>
            <el-button size="small" type="success" plain @click="triggerJavaScan" :loading="javaScanning">
              <i class="el-icon-document-copy"></i> 重新扫描
            </el-button>
            <el-button size="small" @click="openScanSettings">
              <i class="el-icon-setting"></i> 扫描设置
            </el-button>
            <el-button size="small" @click="openScanLabelDialog" :disabled="!hasScannedFiles">
              <i class="el-icon-edit-outline"></i> 人工标注
            </el-button>
            <el-button class="change-project-btn" @click="switchProject">
              <i class="el-icon-refresh"></i> 切换项目
            </el-button>
          </div>
        </div>

        <!-- 模块卡片 -->
        <div class="card">
          <div class="card-header">
            <h3><i class="el-icon-files"></i> 项目模块</h3>
            <el-tag type="success" size="small">{{ modules.length }} 个模块</el-tag>
            <el-button class="card-header-right" size="small" @click="addMoreModules">
              <i class="el-icon-plus"></i> 添加模块
            </el-button>
          </div>

          <div class="module-grid" v-if="modules.length > 0">
            <div
                v-for="module in modules"
                :key="module.id"
                :class="['module-card', getModuleColorClass(module)]"
                @click="handleModuleClick(module)"
            >
              <div class="module-card-icon">
                <i :class="getModuleIconClass(module)"></i>
              </div>
              <div class="module-card-name">{{ module.name }}</div>
              <div class="module-card-desc">{{ module.description || module.code }}</div>
              <div class="module-card-footer">
                <span :class="['module-card-tag', getModuleTagClass(module)]">{{ getModuleType(module) }}</span>
              </div>
              <i class="el-icon-s-promotion module-card-gencode" @click.stop="handleGenerateCode(module)" title="生成代码"></i>
              <i class="el-icon-setting module-card-config" @click.stop="handleConfigSource(module)" title="配置数据源"></i>
              <i class="el-icon-delete module-card-del" @click.stop="handleDeleteModule(module)"></i>
            </div>
          </div>

          <!-- 空模块状态 -->
          <div v-if="modules.length === 0" class="empty-state">
            <i class="el-icon-folder-opened empty-icon"></i>
            <p>该项目下暂无模块</p>
            <small>点击上方「添加模块」按钮开始录入模块</small>
            <el-button type="primary" size="small" @click="addMoreModules" style="margin-top:12px;">添加模块</el-button>
          </div>
        </div>

        <!-- 源码扫描状态 -->
        <div class="card" v-if="modelScanDone || javaScanning">
          <div class="card-header">
            <h3><i class="el-icon-document-copy"></i> 源码数字化模型</h3>
            <el-tag v-if="!javaScanning" size="small" type="success">已扫描</el-tag>
            <el-tag v-else size="small" type="warning">扫描中</el-tag>
          </div>
          <div class="card-body">
            <div v-if="javaScanning" class="scanning-state" style="padding:20px;">
              <i class="el-icon-loading scanning-spin"></i>
              <p>正在扫描 Java 源码，请稍候…</p>
            </div>
            <div v-else style="display:flex;gap:16px;flex-wrap:wrap;">
              <div class="stat-item" style="min-width:60px;flex:0 0 auto;">
                <div class="stat-num" style="font-size:16px;">{{ scanSummary.total || modelScanCount }}</div>
                <div class="stat-label">总文件</div>
              </div>
              <div class="stat-item" style="min-width:60px;flex:0 0 auto;">
                <div class="stat-num" style="font-size:16px;color:#E6A23C;">{{ scanSummary.enums }}</div>
                <div class="stat-label">枚举</div>
              </div>
              <div class="stat-item" style="min-width:60px;flex:0 0 auto;">
                <div class="stat-num" style="font-size:16px;color:#409EFF;">{{ scanSummary.entities }}</div>
                <div class="stat-label">实体</div>
              </div>
              <div class="stat-item" style="min-width:60px;flex:0 0 auto;">
                <div class="stat-num" style="font-size:16px;color:#67C23A;">{{ scanSummary.controllers }}</div>
                <div class="stat-label">控制器</div>
              </div>
              <div class="stat-item" style="min-width:60px;flex:0 0 auto;">
                <div class="stat-num" style="font-size:16px;color:#909399;">{{ scanSummary.services }}</div>
                <div class="stat-label">服务</div>
              </div>
              <div class="stat-item" style="min-width:60px;flex:0 0 auto;">
                <div class="stat-num" style="font-size:16px;color:#C0C4CC;">{{ scanSummary.others }}</div>
                <div class="stat-label">其他</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="workspace-sidebar">
        <div class="sidebar-card">
          <h4><i class="el-icon-data-analysis"></i> 项目统计</h4>
          <div class="stat-row">
            <div class="stat-item">
              <div class="stat-num">{{ modules.length }}</div>
              <div class="stat-label">模块数</div>
            </div>
            <div class="stat-item">
              <div class="stat-num" style="color:#67C23A;">{{ totalTableCount }}</div>
              <div class="stat-label">数据表</div>
            </div>
            <div class="stat-item">
              <div class="stat-num" style="color:#E6A23C;">{{ totalFieldCount }}</div>
              <div class="stat-label">字段数</div>
            </div>
          </div>
        </div>
        <div class="sidebar-card">
          <h4><i class="el-icon-magic-stick"></i> 智慧功能</h4>
          <div class="action-btn-list">
            <AICreateTableButton class="action-btn" style="margin:0;">智能建表</AICreateTableButton>
            <AICreateCodeButton class="action-btn" style="margin:0;">智能生成代码</AICreateCodeButton>
            <el-button class="action-btn" type="warning" plain icon="el-icon-open">便捷开发</el-button>
            <el-button class="action-btn" type="success" plain icon="el-icon-user">人脸SDK集成项目下载</el-button>
            <el-button class="action-btn" type="primary" plain icon="el-icon-document">查看模块详情</el-button>
          </div>
        </div>
        <div class="sidebar-card ai-assistant-card">
          <ai-assistant-panel
            :current-step="currentStep"
            :project-type-label="projectTypeLabel"
            :project-context="{ projectName: currentProjectName, moduleCount: modules.length, scannedFileCount: modelScanCount }"
            @action="handleAIAction"
          />
        </div>
      </div>
    </div>

    <!-- 项目表单弹窗 -->
    <projectForm/>

    <!-- 数据源配置弹窗 -->
    <SourceConfigForm
        :visible.sync="sourceDialogVisible"
        :source-options="$store.state.data.sourceConfig.sourceOptions"
        :initial-form="sourceConfigForm"
        @submit="onSourceConfigSubmit"
        @go-source="goToSourcePage"
    />

    <!-- 批量数据源配置弹窗 -->
    <BatchSourceConfig
        :visible.sync="batchDialogVisible"
        :modules="modules"
        :source-options="$store.state.data.sourceConfig.sourceOptions"
        @go-source="goToSourcePageFromBatch"
    />

    <!-- 智能建表向导弹窗 -->
    <SmartTableWizard
        :visible="wizardVisible"
        :module-id="wizardModuleId"
        :module-name="wizardModuleName"
        :project-id="queryParams.projectId"
        :initial-config-id="wizardConfigId"
        :prefix="wizardPrefix"
        @close="wizardVisible = false"
    />

    <!-- 人工标注弹窗 -->
    <ScanLabelDialog
        :visible="scanLabelDialogVisible"
        :files="scanLabelFiles"
        :ai-config="$ai || {}"
        @update:visible="scanLabelDialogVisible = $event"
        @confirm="onScanLabelConfirm"
    />

    <!-- 扫描策略配置弹窗 -->
    <ScanStrategyDialog
        ref="strategyDialog"
        :visible="scanStrategyDialogVisible"
        :project-id="queryParams.projectId"
        :project-name="currentProjectName"
        :project-code="currentProjectCode"
        :initial-config="strategyConfig"
        @update:visible="onStrategyDialogVisibleChange"
        @confirm="onScanStrategyConfirm"
        @skip="onScanStrategySkip"
        @openScriptGenerator="openScriptGenerator"
    />

    <!-- AI 脚本生成弹窗 -->
    <ScriptGeneratorDialog
        :visible="scriptGeneratorDialogVisible"
        :project-id="queryParams.projectId"
        :edit-script="editingScript"
        @update:visible="scriptGeneratorDialogVisible = $event"
        @saved="onScriptSaved"
    />

    <!-- 模块扫描结果弹窗 -->
    <el-dialog
        :visible="moduleScanDialogVisible"
        width="800px"
        :title="'扫描结果 - ' + (selectedModuleForScan ? selectedModuleForScan.name : '')"
        :close-on-click-modal="false"
        append-to-body
        @update:visible="moduleScanDialogVisible = $event"
        @closed="moduleScanDialogVisible = false"
    >
      <div v-if="selectedModuleFiles.length === 0" style="text-align:center;padding:30px;color:#909399;">
        <i class="el-icon-document" style="font-size:48px;display:block;margin-bottom:12px;"></i>
        <p>该模块暂无扫描数据</p>
      </div>
      <el-table
          v-else
          :data="selectedModuleFiles"
          border stripe size="small"
          style="width:100%;"
          max-height="450"
      >
        <el-table-column label="文件名" prop="simpleName" min-width="180" />
        <el-table-column label="类型" width="120" align="center">
          <template v-slot="{ row }">
            <el-tag :type="scanTagType(row)" size="mini">{{ row.type || '未分类' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="业务编码" prop="code" width="120" />
        <el-table-column label="分类方式" width="100" align="center">
          <template v-slot="{ row }">
            <span v-if="row.classifiedBy" style="font-size:12px;color:#909399;">{{ classifiedByLabel(row.classifiedBy) }}</span>
            <span v-else style="font-size:12px;color:#C0C4CC;">-</span>
          </template>
        </el-table-column>
        <el-table-column label="描述" prop="description" min-width="160" show-overflow-tooltip />
      </el-table>
    </el-dialog>

    <!-- 模块操作弹窗 -->
    <el-dialog
        :visible="moduleActionDialogVisible"
        width="400px"
        :title="activeModule ? activeModule.name : ''"
        :close-on-click-modal="false"
        append-to-body
        @update:visible="moduleActionDialogVisible = $event"
        @closed="moduleActionDialogVisible = false"
    >
      <div style="padding:0;">
        <p style="font-size:13px;color:#909399;margin:0 0 16px;text-align:center;">{{ activeModule ? (activeModule.description || activeModule.code) : '' }}</p>
        <div style="display:flex;flex-direction:column;gap:12px;">
          <el-button
              size="medium"
              icon="el-icon-document-copy"
              @click="showModuleScanResults"
              style="height:44px;font-size:14px;"
          >查看扫描结果</el-button>
          <el-button
              size="medium"
              type="warning"
              plain
              icon="el-icon-s-promotion"
              @click="handleGenerateCode(activeModule)"
              style="height:44px;font-size:14px;"
          >生成代码</el-button>
          <el-button
              size="medium"
              plain
              icon="el-icon-setting"
              @click="handleConfigSource(activeModule); moduleActionDialogVisible = false"
              style="height:44px;font-size:14px;"
          >配置数据源</el-button>
        </div>
      </div>
      <template #footer>
        <el-button @click="moduleActionDialogVisible = false">取消</el-button>
      </template>
    </el-dialog>

</template>
<script>
/**
 * 工作台 - 项目管理与模块扫描
 * @author PeterLee
 * @since 2025-05-07
 */
import AICreateTableButton from "@/views/data/workSpace/components/AICreateTableButton.vue";
import AICreateCodeButton from "@/views/data/workSpace/components/AICreateCodeButton.vue";
import SmartTableWizard from "@/views/data/workSpace/components/SmartTableWizard.vue";
import projectForm from "@/views/data/project/ProjectForm";
import projectDataProvider from "@/views/data/project/ProjectDataProvider";
import sourceConfigDataProvider from "@/views/data/workSpace/SourceConfigDataProvider";
import SourceConfigForm from "@/views/data/workSpace/SourceConfigForm";
import BatchSourceConfig from "@/views/data/workSpace/BatchSourceConfig";
import { registerStore, unregisterStore } from "@/utils/common";
import { getProjectList, getModuleListByProjectId, createModule, deleteModule, getTableCountByProjectId, getFieldCountByProjectId, getModuleWithConfig, bindModuleConfig, saveModuleConfig, batchSaveProjectModel, getProjectModelList, updateProject, getProject, getScanScriptList } from "@/api/dataApi";
import { getList as scaffoldGetList, addData as scaffoldAddData } from "@/api/baseApi";
import { mapMutations, mapState } from "vuex";
import ScanLabelDialog from "./components/ScanLabelDialog.vue";
import ScanStrategyDialog from "./components/ScanStrategyDialog.vue";
import ScriptGeneratorDialog from "./components/ScriptGeneratorDialog.vue";
import ConfigSchemaForm from "@/components/widget/ConfigSchemaForm.vue";
import AIAssistantPanel from "./components/AIAssistantPanel.vue";
import { detectProjectType, walkDirectory as analyzerWalkDir, callAI } from "@/utils/aiProjectAnalyzer";
import { scanSourceFiles, classifyByRules, classifyByAI, summarizeScan, getClassifierConfig } from "@/utils/sourceScanner";
import JSZip from 'jszip';

export default {
  components: {
    AICreateTableButton,
    AICreateCodeButton,
    SmartTableWizard,
    projectForm,
    SourceConfigForm,
    BatchSourceConfig,
    ScanLabelDialog,
    ScanStrategyDialog,
    ScriptGeneratorDialog,
    ConfigSchemaForm,
    AIAssistantPanel
  },
  name: "WorkSpace",
  data() {
    return {
      queryParams: {
        projectId: null
      },
      projectOptions: [],
      // 脚手架创建模式
      createMode: 'existing',
      scaffoldOptions: [],
      selectedScaffoldId: null,
      newProjectName: '',
      creatingFromScaffold: false,
      projectScaffoldSchema: null,
      projectConfigValues: {},
      savingConfig: false,
      modules: [],
      hasModules: false,
      currentStep: 0,
      selectedPath: '',
      combinedModules: [],
      scanning: false,
      submitting: false,
      directoryHandle: null,
      checkAll: false,
      totalTableCount: 0,
      totalFieldCount: 0,
      wizardFromModules: false,
      sourceDialogVisible: false,
      batchDialogVisible: false,
      configuringModule: null,
      sourceConfigForm: {},
      wizardVisible: false,
      wizardModuleId: null,
      wizardModuleName: '',
      wizardConfigId: null,
      wizardPrefix: '',
      // Java 源码扫描
      javaScanning: false,
      javaModels: [],
      scanSummary: { total: 0, enums: 0, entities: 0, controllers: 0, services: 0, others: 0 },
      modelScanDone: false,
      modelScanCount: 0,
      // 扫描策略
      scanMode: 'auto',    // 'auto' = 预制规则+AI, 'manual' = 仅收集（人工标注）
      scanLabelDialogVisible: false,
      scanLabelFiles: [],
      // 扫描策略配置
      scanStrategyDialogVisible: false,
      strategyConfig: { strategyType: 'default', aiDisclosure: 'COMPACT', scanScriptId: null },
      scriptGeneratorDialogVisible: false,
      pendingStartScan: false,
      // 模块操作弹窗
      moduleActionDialogVisible: false,
      activeModule: null,
      // 模块扫描结果查看
      moduleScanDialogVisible: false,
      selectedModuleForScan: null,
      selectedModuleFiles: [],
      // 是否正在使用自定义脚本扫描
      usingCustomScript: false,
      customScriptContent: '',
      // 强制刷新扫描（重新扫描时使用，不清空已有数据）
      forceRefreshScan: false,
      // 自动检测的模块路径（由 detectProjectPaths 填充）
      detectedModulePaths: {},
      // 正在编辑的脚本（用于 ScriptGeneratorDialog）
      editingScript: null,
      wizardActive: false,  // 保持向导模式（即使 hasModules = true）
      // AI 驱动
      projectTypeKey: null,  // 项目类型 key: MAVEN | NODE | PYTHON | GO | etc.
      projectTypeLabel: '',  // 项目类型中文名
      scanProgressText: '',  // 扫描进度提示
      aiModuleAnalyzing: false,  // AI 模块分析中
    }
  },
  computed: {
    ...mapState({
      projectFormInfo: state => state.data && state.data.project ? state.data.project.formInfo : {}
    }),
    hasSourceOptions() {
      return this.$store.state.data.sourceConfig && this.$store.state.data.sourceConfig.sourceOptions.length > 0;
    },
    currentProjectName() {
      if (!this.queryParams.projectId) return '未选择项目';
      const project = this.projectOptions.find(p => p.id === this.queryParams.projectId);
      return project ? project.name : '未选择项目';
    },
    currentProjectPath() {
      if (!this.queryParams.projectId) return '';
      const project = this.projectOptions.find(p => p.id === this.queryParams.projectId);
      return project ? (project.path || project.localPath || '') : '';
    },
    currentProjectCode() {
      if (!this.queryParams.projectId) return '';
      const project = this.projectOptions.find(p => p.id === this.queryParams.projectId);
      return project ? project.code : '';
    },
    newModules() {
      return this.combinedModules.filter(m => m.status === 'new');
    },
    existingMatchModules() {
      return this.combinedModules.filter(m => m.status === 'existing');
    },
    orphanModules() {
      return this.combinedModules.filter(m => m.status === 'orphan');
    },
    checkedModuleCount() {
      return this.newModules.filter(m => m.checked).length;
    },
    hasScannedFiles() {
      return this.javaModels.length > 0 || this.scanLabelFiles.length > 0;
    }
  },
  watch: {
    'projectFormInfo.dialogVisible'(newVal, oldVal) {
      if (oldVal && !newVal) {
        this.handleProjectFormClosed();
      }
    }
  },
  created() {
    registerStore('data', 'project', projectDataProvider);
    registerStore('data', 'sourceConfig', sourceConfigDataProvider);
    this.initData();
  },
  destroyed() {
    unregisterStore('data', 'project');
    unregisterStore('data', 'sourceConfig');
  },
  methods: {
    ...mapMutations({
      clickAdd: 'data/project/clickAdd'
    }),
    async initData() {
      await this.loadProjectList();
      await this.loadScaffoldList();
    },
    async loadProjectList() {
      try {
        const res = await getProjectList();
        if (res.data && res.data.length > 0) {
          this.projectOptions = res.data;
          if (!this.queryParams.projectId || !this.projectOptions.find(p => p.id === this.queryParams.projectId)) {
            this.queryParams.projectId = res.data[0].id;
            await this.loadModules();
          }
        }
      } catch (error) {
        console.error('加载项目列表失败:', error);
      }
    },
    async loadScaffoldList() {
      try {
        const res = await scaffoldGetList("data", "scaffold");
        if (res.code === 200 && res.data) {
          this.scaffoldOptions = res.data;
        }
      } catch (error) {
        console.error('加载脚手架列表失败:', error);
      }
    },
    async createProjectFromScaffold() {
      if (!this.selectedScaffoldId || !this.newProjectName) return;
      // 先在用户手势中弹出目录选择器（必须在任何 await 之前）
      let dirHandle = null;
      const hasFileSystemAPI = typeof window.showDirectoryPicker === 'function';
      if (hasFileSystemAPI) {
        try {
          dirHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
        } catch (e) {
          if (e.name === 'AbortError') return; // 用户取消选择
        }
      }

      this.creatingFromScaffold = true;
      try {
        const scaffoldInfo = this.scaffoldOptions.find(b => b.id === this.selectedScaffoldId);
        // 创建项目并绑定脚手架
        const res = await scaffoldAddData("data", "project", {
          name: this.newProjectName,
          code: this.newProjectName,
          scaffoldId: this.selectedScaffoldId,
          scaffoldVersion: scaffoldInfo ? scaffoldInfo.version : undefined
        });
        if (res.code === 200) {
          const newProjectId = res.data?.id || res.data;
          this.$message.success('项目创建成功');

          // 自动下载并解压脚手架压缩包
          if (scaffoldInfo && scaffoldInfo.scaffoldFilePath) {
            if (dirHandle) {
              try {
                await this.downloadAndExtractScaffold(scaffoldInfo.id, dirHandle);
              } catch (extractErr) {
                console.error('自动解压失败:', extractErr);
                this.$message.warning('自动解压失败: ' + (extractErr.message || extractErr) + '，请手动下载解压');
                window.open('/api/data/scaffold/download/' + scaffoldInfo.id, '_blank');
              }
            } else {
              this.$message.warning('当前浏览器不支持自动解压，请手动下载解压');
              window.open('/api/data/scaffold/download/' + scaffoldInfo.id, '_blank');
            }
          }

          // 切换到新创建的项目
          this.createMode = 'existing';
          this.selectedScaffoldId = null;
          this.newProjectName = '';
          await this.loadProjectList();
          if (newProjectId) {
            this.queryParams.projectId = newProjectId;
            await this.loadModules();
          }
          if (scaffoldInfo) {
            await this.loadScaffoldConfig(scaffoldInfo.id);
            if (this.projectScaffoldSchema) {
              this.wizardActive = true;
              this.currentStep = 1;
            } else if (this.selectedPath) {
              this.wizardActive = true;
              this.currentStep = 2;
            }
          }
        } else {
          this.$message.error(res.message || '项目创建失败');
        }
      } catch (error) {
        console.error('创建项目失败:', error);
        this.$message.error('创建项目失败');
      } finally {
        this.creatingFromScaffold = false;
      }
    },

    async downloadAndExtractScaffold(scaffoldId, dirHandle) {
      this.directoryHandle = dirHandle;
      this.selectedPath = dirHandle.name;

      this.$message.info('正在下载脚手架压缩包...');
      const response = await fetch('/api/data/scaffold/download/' + scaffoldId);
      if (!response.ok) throw new Error('下载失败: HTTP ' + response.status + ' ' + response.statusText);
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('octet-stream') && !contentType.includes('zip')) {
        const text = await response.text().catch(() => '');
        throw new Error('响应不是压缩包 (' + contentType + '): ' + text.substring(0, 200));
      }
      const zipBlob = await response.blob();
      if (zipBlob.size === 0) throw new Error('下载的压缩包为空');
      this.$message.info('下载完成 (' + (zipBlob.size / 1024 / 1024).toFixed(1) + 'MB)，正在解压...');

      this.$message.info('正在解压到 ' + dirHandle.name + '...');
      const zip = await JSZip.loadAsync(zipBlob);
      const entries = Object.keys(zip.files).filter(name => !zip.files[name].dir);
      if (entries.length === 0) throw new Error('压缩包内没有文件');

      let extractedCount = 0;
      for (const filePath of entries) {
        const file = zip.files[filePath];
        const parts = filePath.split('/');
        const fileName = parts.pop();
        if (!fileName) continue;

        let currentHandle = dirHandle;
        for (const dirName of parts) {
          currentHandle = await currentHandle.getDirectoryHandle(dirName, { create: true });
        }

        const content = await file.async('uint8array');
        const fileHandle = await currentHandle.getFileHandle(fileName, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(content);
        await writable.close();
        extractedCount++;
      }

      this.$message.success('脚手架解压完成，共 ' + extractedCount + ' 个文件');
    },
    async loadModules() {
      if (!this.queryParams.projectId) {
        this.modules = [];
        this.hasModules = false;
        return;
      }
      try {
        const res = await getModuleListByProjectId(this.queryParams.projectId);
        this.modules = res.data || [];
        this.hasModules = this.modules.length > 0;
        this.loadModuleStats();
        this.loadScanDataSilent();
      } catch (error) {
        console.error('加载模块列表失败:', error);
        this.modules = [];
        this.hasModules = false;
      }
    },
    async loadModuleStats() {
      if (!this.queryParams.projectId || this.modules.length === 0) {
        this.totalTableCount = 0;
        this.totalFieldCount = 0;
        return;
      }
      try {
        const [tableRes, fieldRes] = await Promise.all([
          getTableCountByProjectId(this.queryParams.projectId).catch(() => ({ data: 0 })),
          getFieldCountByProjectId(this.queryParams.projectId).catch(() => ({ data: 0 }))
        ]);
        this.totalTableCount = tableRes.data || 0;
        this.totalFieldCount = fieldRes.data || 0;
      } catch (error) {
        this.totalTableCount = 0;
        this.totalFieldCount = 0;
      }
    },
    clickAddProject() {
      this.clickAdd();
    },
    async handleProjectFormClosed() {
      await this.loadProjectList();
    },
    async projectChange() {
      this.resetGuide();
      await this.loadModules();
      // 加载脚手架配置
      const project = this.projectOptions.find(p => p.id === this.queryParams.projectId);
      if (project && project.scaffoldId) {
        await this.loadScaffoldConfig(project.scaffoldId);
        await this.loadProjectConfig(this.queryParams.projectId);
      } else {
        this.projectScaffoldSchema = null;
        this.projectConfigValues = {};
      }
    },
    async loadScaffoldConfig(scaffoldId) {
      if (!scaffoldId) {
        this.projectScaffoldSchema = null;
        return;
      }
      const cached = this.scaffoldOptions.find(s => s.id === scaffoldId);
      if (cached) {
        this.projectScaffoldSchema = cached.configSchema || null;
        return;
      }
      this.projectScaffoldSchema = null;
    },
    async loadProjectConfig(projectId) {
      if (!projectId) {
        this.projectConfigValues = {};
        return;
      }
      try {
        const res = await getProject(projectId);
        if (res.code === 200 && res.data) {
          const raw = res.data.configValues;
          this.projectConfigValues = raw ? JSON.parse(raw) : {};
        }
      } catch (e) {
        this.projectConfigValues = {};
      }
    },
    async confirmConfig() {
      if (!this.projectScaffoldSchema) {
        this.goToStep(2);
        return;
      }
      this.savingConfig = true;
      try {
        const project = this.projectOptions.find(p => p.id === this.queryParams.projectId);
        await updateProject({
          id: this.queryParams.projectId,
          code: project ? project.code : this.newProjectName,
          name: project ? project.name : this.newProjectName,
          configValues: JSON.stringify(this.projectConfigValues)
        });
        this.$message.success('配置已保存');
        this.goToStep(2);
      } catch (e) {
        console.error('保存配置失败:', e);
        this.$message.error('保存配置失败');
      } finally {
        this.savingConfig = false;
      }
    },
    goToStep(step) {
      this.currentStep = step;
    },
    resetGuide() {
      this.currentStep = 0;
      this.selectedPath = '';
      this.combinedModules = [];
      this.checkAll = false;
    },
    switchProject() {
      this.wizardActive = false;
      this.wizardFromModules = this.modules.length > 0;
      this.hasModules = false;
      this.resetGuide();
    },
    addMoreModules() {
      this.wizardFromModules = this.modules.length > 0;
      this.hasModules = false;
      this.currentStep = 2;
    },
    returnToModules() {
      this.hasModules = true;
      this.wizardFromModules = false;
    },
    handleAIAction(action) {
      switch (action) {
        case 'generateCode':
          // 导航到模块列表中的第一个有扫描结果的模块
          this.$message.info('请点击模块卡片上的代码生成图标');
          break;
        case 'configureScaffold':
          this.goToStep(1);
          break;
        case 'startScan':
          this.triggerJavaScan();
          break;
      }
    },
    async selectDirectory() {
      if (!this.queryParams.projectId) {
        this.$message.warning('请先选择项目');
        return;
      }
      try {
        if (!window.showDirectoryPicker) {
          this.$message.error('当前浏览器不支持 File System Access API，请使用 Chrome 86+ 或 Edge 86+');
          return;
        }
        this.directoryHandle = await window.showDirectoryPicker();
        this.selectedPath = this.directoryHandle.name;
        // 检测项目类型
        await this.detectProjectTypeFromDir();

        if (this.projectTypeKey === 'MAVEN' || this.projectTypeKey === 'GRADLE') {
          // Maven/Gradle 项目走原有 pom.xml 扫描
          this.scanProgressText = `检测到 ${this.projectTypeLabel} 项目，正在扫描 pom.xml...`;
          await this.scanPomXml();
          if (this.combinedModules.length > 0) {
            this.currentStep = 3;
          }
        } else {
          // 非 Maven 项目：提示使用 AI 分析
          this.combinedModules = [];
          this.scanProgressText = `检测到 ${this.projectTypeLabel || '非 Maven'} 项目，使用 AI 分析模块结构`;
          this.$message.info(`检测到 ${this.projectTypeLabel || '非 Maven'} 项目，将使用 AI 分析项目模块结构`);
          this.currentStep = 3; // 跳转到 Step 3，显示 AI 分析模式
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('选择目录失败:', error);
          this.$message.error('选择目录失败');
        }
      }
    },
    async rescanDirectory() {
      if (!this.directoryHandle) {
        this.$message.warning('请先选择目录');
        return;
      }
      await this.detectProjectTypeFromDir();
      if (this.projectTypeKey === 'MAVEN' || this.projectTypeKey === 'GRADLE') {
        await this.scanPomXml();
      } else {
        await this.analyzeModulesByAI();
      }
    },
    async scanPomXml() {
      if (!this.directoryHandle) {
        this.$message.warning('请先选择目录');
        return;
      }
      this.scanning = true;
      this.combinedModules = [];
      this.checkAll = false;
      const rawModules = [];
      try {
        await this.collectModulesFromDir(this.directoryHandle, rawModules);
        this.mergeWithExisting(rawModules);
        if (this.combinedModules.length === 0) {
          this.$message.info('未找到任何模块');
        } else {
          const newCount = this.newModules.length;
          this.$message.success(`扫描完成，共 ${this.combinedModules.length} 个模块（${newCount} 个可录入）`);
          this.checkAll = newCount > 0;
        }
      } catch (error) {
        console.error('扫描失败:', error);
        this.$message.error('扫描失败');
      } finally {
        this.scanning = false;
      }
    },
    async detectProjectTypeFromDir() {
      if (!this.directoryHandle) return;
      try {
        const { files } = await analyzerWalkDir(this.directoryHandle);
        const result = detectProjectType(files);
        if (result.primary) {
          this.projectTypeKey = result.primary.key;
          this.projectTypeLabel = result.primary.label;
        }
      } catch (e) {
        console.warn('项目类型检测失败:', e);
        this.projectTypeKey = null;
        this.projectTypeLabel = '';
      }
    },
    async analyzeModulesByAI() {
      if (!this.directoryHandle) {
        this.$message.warning('请先选择项目目录');
        return;
      }
      this.aiModuleAnalyzing = true;
      this.scanning = true;
      this.combinedModules = [];
      try {
        // 遍历目录获取文件清单
        const { files, structureLines } = await analyzerWalkDir(this.directoryHandle);
        const structureTree = structureLines.join('\n').substring(0, 5000);

        // 构建 AI prompt
        const prompt = `你是一个项目架构分析师。请分析以下 ${this.projectTypeLabel || '项目'} 的目录结构，识别出项目的模块/组件划分。

项目类型: ${this.projectTypeLabel || '未知'}
关键文件: ${files.filter(f => f.name === 'package.json' || f.name === 'pom.xml' || f.name === 'build.gradle' || f.name === 'go.mod' || f.name === 'Cargo.toml' || f.name === 'setup.py').map(f => f.path).join(', ')}

## 目录结构
${structureTree}

## 要求
从目录结构中识别出项目的模块划分。常见的多模块结构包括：
- Maven/Gradle: api, service, common, web, gateway 等
- Node.js: packages/, apps/, libs/ 等（monorepo）
- Python: Django apps, FastAPI 模块等
- Go: cmd/, pkg/, internal/ 等

请返回纯 JSON 数组（不要 markdown 代码块）：
[
  {"name": "模块名", "type": "api|web|service|common|gateway|module", "description": "模块描述"}
]

如果目录结构不足以识别多模块，返回一个代表主应用的模块。
尽量根据实际目录结构推断，不要编造。`;

        const aiResult = await callAI(prompt, this.$ai);
        const aiModules = Array.isArray(aiResult) ? aiResult : (aiResult.modules || []);

        if (aiModules.length === 0) {
          this.$message.info('AI 未能识别出模块结构，请手动录入');
          return;
        }

        // 转换为 combinedModules 格式
        const rawModuleNames = aiModules.map(m => m.name);
        this.mergeWithExisting(rawModuleNames);

        // 补充类型信息
        this.combinedModules.forEach(item => {
          const aiMatch = aiModules.find(m => m.name === item.name);
          if (aiMatch) {
            item.description = aiMatch.description || '';
            item.moduleType = aiMatch.type || 'module';
          }
        });

        this.$message.success(`AI 分析完成，识别到 ${this.combinedModules.length} 个模块（${this.newModules.length} 个可录入）`);
        this.checkAll = this.newModules.length > 0;
      } catch (error) {
        console.error('AI 模块分析失败:', error);
        this.$message.error('AI 分析失败: ' + (error.message || '未知错误'));
      } finally {
        this.aiModuleAnalyzing = false;
        this.scanning = false;
      }
    },
    async collectModulesFromDir(dirHandle, result) {
      try {
        for await (const [name, handle] of dirHandle.entries()) {
          if (handle.kind === 'file' && name === 'pom.xml') {
            const file = await handle.getFile();
            const text = await file.text();
            const modules = this.parseModulesFromPom(text);
            modules.forEach(m => {
              if (!result.includes(m)) {
                result.push(m);
              }
            });
          } else if (handle.kind === 'directory') {
            await this.collectModulesFromDir(handle, result);
          }
        }
      } catch (error) {
        console.error('遍历目录出错:', error);
      }
    },
    mergeWithExisting(scanNames) {
      const existingNames = new Set(this.modules.map(m => m.name));
      const scanSet = new Set(scanNames);
      const result = [];

      // 1. 新增模块：本地有，但项目中不存在
      scanNames.forEach(name => {
        if (!existingNames.has(name)) {
          result.push({ name, status: 'new', checkable: true, checked: true });
        }
      });

      // 2. 已录入模块：本地有，且项目中已存在（默认勾选，不可取消）
      scanNames.forEach(name => {
        if (existingNames.has(name)) {
          result.push({ name, status: 'existing', checkable: false, checked: true });
        }
      });

      // 3. 仅存在于项目：项目中有，但本地未找到
      this.modules.forEach(m => {
        if (!scanSet.has(m.name)) {
          result.push({ name: m.name, status: 'orphan', checkable: false, checked: false });
        }
      });

      this.combinedModules = result;
    },
    parseModulesFromPom(pomContent) {
      const modules = [];
      const moduleRegex = /<module>([^<]+)<\/module>/g;
      let match;
      while ((match = moduleRegex.exec(pomContent)) !== null) {
        const moduleName = match[1].trim();
        if (moduleName) {
          modules.push(moduleName);
        }
      }
      return modules;
    },
    toggleScanItem(item) {
      if (!item.checkable) return;
      this.$set(item, 'checked', !item.checked);
      this.checkAll = this.newModules.every(m => m.checked);
    },
    toggleCheckAll(val) {
      this.newModules.forEach(m => {
        this.$set(m, 'checked', val);
      });
    },
    resetScan() {
      this.combinedModules = [];
      this.checkAll = false;
      this.currentStep = 2;
    },
    async submitModules() {
      const toSubmit = this.newModules.filter(m => m.checked);
      if (toSubmit.length === 0) {
        this.$message.warning('请至少选择一个新增模块');
        return;
      }
      this.submitting = true;
      try {
        for (const item of toSubmit) {
          await createModule({
            name: item.name,
            code: item.name,
            projectId: this.queryParams.projectId
          });
        }
        this.$message.success(`成功录入 ${toSubmit.length} 个模块`);
        await this.loadModules();
        // 进入源码扫描标注步骤
        this.wizardActive = true;
        this.currentStep = 5;
        // 弹出扫描策略配置
        await this.loadProjectStrategy();
        this.pendingStartScan = true;
        this.scanStrategyDialogVisible = true;
      } catch (error) {
        console.error('录入失败:', error);
        this.$message.error('录入失败');
      } finally {
        this.submitting = false;
      }
    },
    getScanItemClass(item) {
      if (item.status === 'existing') return 'module-item-existing';
      if (item.status === 'orphan') return 'module-item-orphan';
      if (item.checked) return 'module-item-checked';
      return '';
    },
    getScanStatusTag(item) {
      if (item.status === 'existing') return 'tag-existing';
      if (item.status === 'orphan') return 'tag-orphan';
      return this.getModuleTagClassStr(item.name);
    },
    getScanStatusLabel(item) {
      if (item.status === 'new') return this.getModuleTypeStr(item.name);
      if (item.status === 'existing') return '已录入';
      return '仅DB';
    },
    getModuleColorClass(module) {
      const name = (module.name || '').toLowerCase();
      if (name.includes('web') || name.includes('gateway')) return 'module-card-green';
      if (name.includes('service')) return 'module-card-orange';
      if (name.includes('common') || name.includes('util') || name.includes('core')) return 'module-card-purple';
      return '';
    },
    getModuleIconClass(module) {
      const name = (module.name || '').toLowerCase();
      if (name.includes('api')) return 'el-icon-connection';
      if (name.includes('web') || name.includes('gateway')) return 'el-icon-monitor';
      if (name.includes('service')) return 'el-icon-setting';
      if (name.includes('common') || name.includes('util') || name.includes('core')) return 'el-icon-files';
      return 'el-icon-folder-opened';
    },
    getModuleType(module) {
      const name = (module.name || '').toLowerCase();
      if (name.includes('api')) return 'API';
      if (name.includes('web')) return 'Web';
      if (name.includes('service')) return 'Service';
      if (name.includes('common') || name.includes('util') || name.includes('core')) return 'Common';
      if (name.includes('gateway')) return 'Gateway';
      return 'Module';
    },
    getModuleTagClass(module) {
      const type = this.getModuleType(module);
      if (type === 'API') return 'tag-api';
      if (type === 'Web' || type === 'Gateway') return 'tag-web';
      if (type === 'Service') return 'tag-service';
      if (type === 'Common') return 'tag-common';
      return 'tag-api';
    },
    handleModuleClick(module) {
      // 点击模块卡片弹出操作面板
      this.activeModule = module;
      this.moduleActionDialogVisible = true;
    },
    async handleConfigSource(module) {
      this.configuringModule = module;
      const name = module.name || '';
      // 先加载模块完整信息（含 configVo + 模块自身代码生成字段）
      let moduleData = null;
      try {
        const res = await getModuleWithConfig(module.id);
        moduleData = res.data;
      } catch (e) {
        console.error('加载模块配置失败:', e);
      }
      const m = moduleData || module;
      const configVo = m.configVo;
      const defaults = {
        moduleName: name,
        dataSourceId: configVo ? configVo.dataSourceId : null,
        name: configVo ? configVo.name : '',
        dataBaseId: configVo ? configVo.id : null,
        authorName: m.authorName || 'Auto Generated',
        packageName: m.packageName || 'com.yida.' + name.toLowerCase().replace(/-/g, '.'),
        prefix: m.prefix || '',
        moduleDescription: m.moduleDescription || module.description || name + ' 模块',
        comment: configVo ? configVo.comment : (name + ' 数据库配置'),
        javaPath: m.javaPath || (this.detectedModulePaths[name] && this.detectedModulePaths[name].javaPath) || '',
        vuePath: m.vuePath || (this.detectedModulePaths[name] && this.detectedModulePaths[name].vuePath) || ''
      };
      await this.$store.dispatch('data/sourceConfig/loadSourceOptions');

      this.sourceConfigForm = defaults;
      this.sourceDialogVisible = true;
    },
    goToSourcePage() {
      this.sourceDialogVisible = false;
      window.open('/#/data/source', '_blank');
    },
    goToSourcePageFromBatch() {
      this.batchDialogVisible = false;
      window.open('/#/data/source', '_blank');
    },
    openBatchConfig() {
      if (this.modules.length === 0) {
        this.$message.warning('该项目下暂无模块');
        return;
      }
      this.$store.dispatch('data/sourceConfig/loadSourceOptions');
      this.batchDialogVisible = true;
    },
    async onSourceConfigSubmit(formData, done) {
      const moduleId = this.configuringModule ? this.configuringModule.id : null;
      if (!moduleId) {
        done();
        return;
      }

      try {
        const dataBaseId = formData.dataBaseId;

        if (!dataBaseId) {
          this.$message.error('请先选择数据库');
          done();
          return;
        }

        await saveModuleConfig(moduleId, dataBaseId, {
          prefix: formData.prefix || '',
          authorName: formData.authorName || '',
          packageName: formData.packageName || '',
          moduleDescription: formData.moduleDescription || '',
          javaPath: formData.javaPath || '',
          vuePath: formData.vuePath || ''
        });
        this.sourceDialogVisible = false;
        done();
        await this.loadModules();
        if (this.wizardModuleId === moduleId) {
          this.$nextTick(() => {
            this.wizardVisible = true;
          });
        }
      } catch (e) {
        console.error('保存模块配置失败:', e);
        this.$message.error('保存配置失败');
        done();
      }
    },
    async handleDeleteModule(module) {
      try {
        await this.$confirm(`确定要删除模块「${module.name}」吗？删除后不可恢复。`, '删除确认', {
          confirmButtonText: '确定删除',
          cancelButtonText: '取消',
          type: 'warning',
          confirmButtonClass: 'el-button--danger'
        });
        await deleteModule(module.id);
        this.$message.success('删除成功');
        await this.loadModules();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除模块失败:', error);
          this.$message.error('删除失败');
        }
      }
    },
    getModuleTagClassStr(moduleName) {
      const name = (moduleName || '').toLowerCase();
      if (name.includes('api')) return 'tag-api';
      if (name.includes('web') || name.includes('gateway')) return 'tag-web';
      if (name.includes('service')) return 'tag-service';
      if (name.includes('common') || name.includes('util') || name.includes('core')) return 'tag-common';
      return 'tag-api';
    },
    getModuleTypeStr(moduleName) {
      const name = (moduleName || '').toLowerCase();
      if (name.includes('api')) return 'API';
      if (name.includes('web')) return 'Web';
      if (name.includes('service')) return 'Service';
      if (name.includes('common') || name.includes('util') || name.includes('core')) return 'Common';
      if (name.includes('gateway')) return 'Gateway';
      return 'Module';
    },

    /* ========== 多语言源码扫描（Java / Node / Python / Go 等） ========== */

    async triggerJavaScan() {
      if (!this.queryParams.projectId) {
        this.$message.warning('请先选择项目');
        return;
      }
      if (!this.directoryHandle) {
        try {
          if (!window.showDirectoryPicker) {
            this.$message.error('当前浏览器不支持 File System Access API');
            return;
          }
          this.directoryHandle = await window.showDirectoryPicker();
          this.selectedPath = this.directoryHandle.name;
        } catch (error) {
          if (error.name !== 'AbortError') {
            this.$message.error('选择目录失败');
          }
          return;
        }
      }
      // 检测项目类型（如果尚未检测）
      if (!this.projectTypeKey) {
        await this.detectProjectTypeFromDir();
      }

      if (this.projectTypeKey === 'MAVEN' || this.projectTypeKey === 'GRADLE') {
        // Java 项目走原有的完整扫描流程
        await this.loadProjectStrategy();
        this.pendingStartScan = true;
        this.forceRefreshScan = true;
        const hasConfig = this.strategyConfig.aiDisclosure || this.strategyConfig.scanScriptId;
        if (hasConfig) {
          this.onScanStrategyConfirm(this.strategyConfig);
        } else {
          this.scanStrategyDialogVisible = true;
        }
      } else {
        // 非 Java 项目走 AI 驱动扫描
        await this.scanNonJavaSources();
      }
    },

    /* ========== 非 Java 项目的 AI 驱动源码扫描 ========== */
    async scanNonJavaSources() {
      if (!this.directoryHandle) {
        this.$message.warning('请先选择项目目录');
        return;
      }
      this.javaScanning = true;
      this.javaModels = [];
      this.scanSummary = {
        total: 0, enums: 0, entities: 0, controllers: 0, services: 0, others: 0
      };
      this.scanProgressText = '正在收集源码文件...';

      try {
        // 1. 收集源码文件
        const models = await scanSourceFiles(this.directoryHandle, this.projectTypeKey || 'GENERIC');
        this.javaModels = models;
        this.scanSummary.total = models.length;
        this.scanProgressText = `已收集 ${models.length} 个源文件，正在进行规则分类...`;

        // 2. 规则分类
        const classifiedCount = classifyByRules(models, this.projectTypeKey || 'GENERIC');
        this.scanProgressText = `规则分类完成（${classifiedCount} 个），AI 补充分类中...`;

        // 3. AI 补充分类
        const unclassified = models.filter(m => !m.type);
        if (unclassified.length > 0 && this.$ai && this.$ai.endpoint) {
          this.scanProgressText = `AI 正在分析 ${unclassified.length} 个未分类文件...`;
          const aiResults = await classifyByAI(
            unclassified,
            this.projectTypeKey || 'GENERIC',
            this.$ai,
            'COMPACT'
          );
          let aiCount = 0;
          for (const r of aiResults) {
            const file = models.find(f => f.filePath === r.filePath);
            if (file && r.type && r.type !== 'OTHER') {
              file.type = r.type;
              file.code = r.code || r.type.toLowerCase();
              file.description = r.description || '';
              file._classifiedBy = 'ai';
              aiCount++;
            }
          }
          this.scanProgressText = `AI 补充分类完成（${aiCount} 个）`;
        }

        // 4. 统计
        const summary = summarizeScan(models);
        this.scanSummary = {
          total: summary.total || models.length,
          entities: summary.ENTITY || summary.MODEL || 0,
          controllers: summary.CONTROLLER || 0,
          services: summary.SERVICE || 0,
          enums: summary.ENUM || 0,
          others: summary.OTHER || 0,
        };

        this.modelScanDone = true;
        this.modelScanCount = models.length;
        this.scanProgressText = '';

        // 5. 提交到后端
        try {
          await this.submitProjectModel();
        } catch (e) {
          console.warn('提交扫描结果失败:', e);
        }

        this.$message.success(`源码扫描完成，共 ${models.length} 个文件`);

        // 6. 未分类文件提示
        const remaining = models.filter(f => !f.type || f.type === 'OTHER');
        if (remaining.length > 0) {
          this.$confirm(
            `仍有 ${remaining.length} 个文件未能分类，是否打开人工标注窗口？`,
            '未完成分类',
            { confirmButtonText: '去标注', cancelButtonText: '以后再说', type: 'warning' }
          ).then(() => {
            this.openScanLabelDialog();
          }).catch(() => {});
        }
      } catch (error) {
        console.error('源码扫描失败:', error);
        this.$message.error('源码扫描失败: ' + (error.message || '未知错误'));
      } finally {
        this.javaScanning = false;
        this.scanProgressText = '';
      }
    },

    /* ========== 管道扫描：规则分类→AI补充→人工兜底 ========== */
    async scanJavaSourcesPipeline() {
      if (!this.directoryHandle) {
        this.$message.warning('请先选择项目目录');
        return;
      }
      this.javaScanning = true;
      this.javaModels = [];
      this.scanSummary = { total: 0, enums: 0, entities: 0, controllers: 0, services: 0, others: 0 };
      try {
        // Step 1: 收集文件（含机械提取 + 规则分类）
        await this.collectJavaFiles(this.directoryHandle, this.javaModels, '');
        this.scanSummary.total = this.javaModels.length;

        // Step 1.5: 自定义脚本分类（如果配置了自定义脚本）
        if (this.usingCustomScript && this.customScriptContent) {
          try {
            const classifyFn = new Function('file', this.customScriptContent + '\n; return classifyFile(file);');
            let customCount = 0;
            for (const m of this.javaModels) {
              if (!m.type || m.type === 'OTHER') {
                try {
                  const result = classifyFn({
                    fileName: m.fileName,
                    filePath: m.filePath,
                    content: m._originalContent || m.description || ''
                  });
                  if (result) {
                    let type = null;
                    if (typeof result === 'string') {
                      type = result;
                    } else if (typeof result === 'object') {
                      // 合并脚本返回的丰富信息
                      if (result.type && result.type !== 'OTHER') {
                        type = result.type;
                        Object.keys(result).forEach(k => {
                          if (k !== 'type' && result[k] !== undefined && result[k] !== null) {
                            // fields/apiPaths 转 JSON 字符串
                            if (k === 'fields') m.fieldsJson = JSON.stringify(result[k]);
                            else if (k === 'apiPaths') m.apiPathsJson = JSON.stringify(result[k]);
                            else if (k === 'constants') m.constantsJson = JSON.stringify(result[k]);
                            else m[k] = result[k];
                          }
                        });
                      }
                    }
                    if (type) {
                      m.type = type;
                      m._classifiedBy = 'CUSTOM_SCRIPT';
                      customCount++;
                    }
                  }
                } catch (e) {
                  console.warn('自定义脚本执行失败:', m.fileName, e);
                }
              }
            }
            if (customCount > 0) {
              this.$message.success(`自定义脚本分类 ${customCount} 个文件`);
            }
          } catch (e) {
            console.error('自定义脚本加载失败:', e);
            this.$message.warning('自定义脚本执行失败，回退到默认规则');
          }
        }

        // Step 2: AI 补充分类（仅对未分类文件，按披露策略控制内容量）
        const disclosure = this.strategyConfig.aiDisclosure || 'COMPACT';
        const unclassified = this.javaModels.filter(f => !f.type || f.type === 'OTHER');
        if (unclassified.length > 0 && this.$ai && this.$ai.endpoint) {
          try {
            const aiResults = await this.classifyByAI(unclassified, disclosure);
            let aiCount = 0;
            aiResults.forEach(r => {
              const file = this.javaModels.find(f => f.fileName === r.fileName);
              if (file && r.type && r.type !== 'OTHER') {
                file.type = r.type;
                file._classifiedBy = 'ai';
                aiCount++;
              }
            });
            if (aiCount > 0) {
              this.$message.success(`AI 补充分类 ${aiCount} 个文件`);
            }

            // PROGRESSIVE: 第一轮后仍有未分类的，追加内容再试一轮
            if (disclosure === 'PROGRESSIVE') {
              const remaining = this.javaModels.filter(f => !f.type || f.type === 'OTHER');
              if (remaining.length > 0) {
                this.$message.info(`渐进式补充分析 ${remaining.length} 个文件...`);
                const aiResults2 = await this.classifyByAI(remaining, 'FULL');
                let aiCount2 = 0;
                aiResults2.forEach(r => {
                  const file = this.javaModels.find(f => f.fileName === r.fileName);
                  if (file && r.type && r.type !== 'OTHER') {
                    file.type = r.type;
                    file._classifiedBy = 'ai';
                    aiCount2++;
                  }
                });
                if (aiCount2 > 0) {
                  this.$message.success(`渐进式补充分类 ${aiCount2} 个文件`);
                }
              }
            }
          } catch (e) {
            console.error('AI分类失败:', e);
          }
        }

        // 重新统计
        for (const m of this.javaModels) {
          if (m.type === 'ENUM') this.scanSummary.enums++;
          else if (m.type === 'ENTITY') this.scanSummary.entities++;
          else if (m.type === 'CONTROLLER') this.scanSummary.controllers++;
          else if (m.type === 'SERVICE' || m.type === 'SERVICE_IMPL') this.scanSummary.services++;
          else this.scanSummary.others++;
        }

        // 提交到后端
        await this.submitProjectModel();
        // 自动检测模块路径
        this.detectProjectPaths();
        this.$message.success(`源码扫描完成，共 ${this.javaModels.length} 个文件`);

        // Step 3: 未分类文件提示人工标注
        const remaining = this.javaModels.filter(f => !f.type || f.type === 'OTHER');
        if (remaining.length > 0) {
          this.$confirm(
            `仍有 ${remaining.length} 个文件未能分类，是否打开人工标注窗口？`,
            '未完成分类',
            { confirmButtonText: '去标注', cancelButtonText: '以后再说', type: 'warning' }
          ).then(() => {
            this.openScanLabelDialog();
          }).catch(() => {});
        }
      } catch (error) {
        console.error('Java源码扫描失败:', error);
        this.$message.error('源码扫描失败: ' + (error.message || '未知错误'));
      } finally {
        this.javaScanning = false;
      }
    },

    /* ========== 仅收集模式：直接打开人工标注 ========== */
    async scanJavaSourcesManual() {
      if (!this.directoryHandle) {
        this.$message.warning('请先选择项目目录');
        return;
      }
      this.javaScanning = true;
      try {
        const rawFiles = [];
        await this.collectJavaFilesRaw(this.directoryHandle, rawFiles, '');
        if (rawFiles.length === 0) {
          this.$message.info('未找到 Java 文件');
          return;
        }
        rawFiles.forEach(f => { f._checked = false; });
        this.scanLabelFiles = rawFiles;
        this.scanLabelDialogVisible = true;
        this.$message.success(`已收集 ${rawFiles.length} 个 Java 文件，请进行人工标注`);
      } catch (error) {
        console.error('收集Java文件失败:', error);
        this.$message.error('收集失败: ' + (error.message || '未知错误'));
      } finally {
        this.javaScanning = false;
      }
    },

    /* ========== 收集原始文件（无分类/无提取） ========== */
    async collectJavaFilesRaw(dirHandle, result, parentPath) {
      try {
        for await (const [name, handle] of dirHandle.entries()) {
          const relativePath = parentPath ? parentPath + '/' + name : name;
          if (handle.kind === 'file' && name.endsWith('.java')) {
            try {
              const file = await handle.getFile();
              const text = await file.text();
              if (text && text.length >= 10) {
                result.push({
                  fileName: name,
                  filePath: relativePath,
                  simpleName: name.replace('.java', ''),
                  content: text,
                  type: ''
                });
              }
            } catch (e) {}
          } else if (handle.kind === 'directory') {
            if (['node_modules', 'target', '.git', '.mvn', 'dist', 'build'].includes(name)) continue;
            await this.collectJavaFilesRaw(handle, result, relativePath);
          }
        }
      } catch (error) {
        console.error('遍历目录出错:', error);
      }
    },

    async collectJavaFiles(dirHandle, result, parentPath) {
      try {
        for await (const [name, handle] of dirHandle.entries()) {
          const relativePath = parentPath ? parentPath + '/' + name : name;
          if (handle.kind === 'file' && name.endsWith('.java')) {
            try {
              const file = await handle.getFile();
              const text = await file.text();
              const model = this.parseJavaFileContent(name, relativePath, text);
              if (model) {
                result.push(model);
              }
            } catch (e) {
              // 跳过无法读取的文件
            }
          } else if (handle.kind === 'directory') {
            // 跳过 node_modules、target、.git 等目录
            if (name === 'node_modules' || name === 'target' || name === '.git' ||
                name === '.mvn' || name === 'dist' || name === 'build') {
              continue;
            }
            await this.collectJavaFiles(handle, result, relativePath);
          }
        }
      } catch (error) {
        console.error('遍历Java目录出错:', error);
      }
    },

    parseJavaFileContent(fileName, filePath, content) {
      const simpleName = fileName.replace('.java', '');
      if (!content || content.length < 10) return null;

      // 提取包名
      const pkgMatch = content.match(/package\s+([\w.]+);/);
      const packageName = pkgMatch ? pkgMatch[1] : '';
      const className = packageName ? packageName + '.' + simpleName : simpleName;

      // 提取类注释（Javadoc中的第一行描述）
      const descMatch = content.match(/\/\*\*[\s\S]*?@(?:author|since|description)\s+([\s\S]*?)\s*\*\//);
      let description = '';
      if (descMatch) {
        description = descMatch[1].split('\n')[0].trim().replace(/\* /g, '').trim();
      }
      if (!description) {
        const simpleDescMatch = content.match(/\/\*\*[\s\S]*?\*\/\s*(?:public\s+)?(?:class|enum|interface)\s+\w+/);
        if (simpleDescMatch) {
          const lines = simpleDescMatch[0].split('\n');
          for (const line of lines) {
            const cleaned = line.replace(/^\s*\*\s?/, '').trim();
            if (cleaned && !cleaned.startsWith('@') && !cleaned.startsWith('/*') && !cleaned.startsWith('*/')) {
              description = cleaned;
              break;
            }
          }
        }
      }
      // 限制描述长度
      if (description && description.length > 200) {
        description = description.substring(0, 200);
      }

      // === 类型分类 ===
      let type = 'OTHER';
      if (content.includes(' enum ')) type = 'ENUM';
      else if (content.includes(' @TableName') || content.includes('@TableName(')) type = 'ENTITY';
      else if (fileName.endsWith('Controller.java')) type = 'CONTROLLER';
      else if (fileName.endsWith('ServiceImpl.java')) type = 'SERVICE_IMPL';
      else if (fileName.endsWith('Service.java')) type = 'SERVICE';
      else if (fileName.endsWith('Mapper.java')) type = 'MAPPER';
      else if (fileName.endsWith('Vo.java') || fileName.endsWith('VO.java')) type = 'VO';
      else if (fileName.endsWith('Dto.java') || fileName.endsWith('DTO.java')) type = 'DTO';
      else if (fileName.endsWith('Config.java') || fileName.endsWith('Configuration.java')) type = 'CONFIG';
      else if (content.includes(' interface ')) type = 'INTERFACE';

      // === 提取模块名（根据路径推断） ===
      const pathParts = filePath.split('/');
      let moduleName = '';
      // 找到 src 之前的路径部分作为模块名
      const srcIdx = pathParts.indexOf('src');
      if (srcIdx > 0) {
        moduleName = pathParts.slice(0, srcIdx).join('/');
      }

      // === 基础模型 ===
      const model = {
        fileName,
        filePath,
        moduleName,
        className,
        simpleName,
        type,
        code: '',
        description,
        tableName: '',
        hasParentId: false,
        isJunction: false,
        hasListApi: false,
        listApiPath: '',
        listModuleCode: '',
        enumCode: '',
        enumLabel: '',
        fieldsJson: '',
        constantsJson: '',
        apiPathsJson: '',
        annotationsJson: '',
        _originalContent: content  // 供自定义脚本使用，不提交到后端
      };

      // === 类型专用提取 ===

      if (type === 'ENTITY') {
        // @TableName
        const tnMatch = content.match(/@TableName\("([^"]+)"\)/);
        if (tnMatch) model.tableName = tnMatch[1];

        // 提取所有字段
        const fields = this.extractEntityFields(content, type);
        model.fieldsJson = JSON.stringify(fields);

        // parentId 检测
        model.hasParentId = content.includes('parentId') || content.includes('parent_id');

        // 关联表检测：只有Long xxxId类型的字段，没有其他业务字段
        const businessFieldCount = fields.filter(f =>
            !f.name.endsWith('Id') && !['id', 'state', 'description', 'createId', 'createOrgId', 'updateId',
              'createDate', 'updateDate', 'deleteFlag', 'createBy', 'updateBy', 'createTime', 'updateTime'].includes(f.name)
        ).length;
        model.isJunction = fields.length > 1 && businessFieldCount === 0;

        // code 取表名或类名
        model.code = model.tableName || simpleName.replace('Entity', '');
      }

      if (type === 'CONTROLLER') {
        // 提取所有 API 路径
        const apiPaths = this.extractControllerApis(content);
        model.apiPathsJson = JSON.stringify(apiPaths);

        // 查找 /xxx/list 接口
        const listApi = apiPaths.find(p => p.method === 'GET' && p.path.match(/\/\w+\/list$/));
        if (listApi) {
          model.hasListApi = true;
          model.listApiPath = listApi.path;
          const codeMatch = listApi.path.match(/\/(\w+)\/list$/);
          if (codeMatch) model.code = codeMatch[1];

          // 优先从 @SaCheckPermission(value = "system:accessLog:list") 解析模块code
          // 权限码格式: moduleCode:businessCode:actionCode
          const permRegex = /@SaCheckPermission\s*\(\s*(?:value\s*=\s*)?"([^"]+)"[^)]*\)/g;
          let permMatch;
          while ((permMatch = permRegex.exec(content)) !== null) {
            const permParts = permMatch[1].split(':');
            if (permParts.length === 3 && permParts[2] === 'list') {
              model.listModuleCode = permParts[0];
              break;
            }
          }
          // 权限码提取失败时，从 listApiPath 取 code 前一个路径段作为备用
          if (!model.listModuleCode) {
            const pathParts = listApi.path.split('/').filter(Boolean);
            const codeIdx = pathParts.lastIndexOf(model.code);
            if (codeIdx > 0) {
              model.listModuleCode = pathParts[codeIdx - 1];
            }
          }
        }

        // 如果没有/list接口，用第一个GET路径的code
        if (!model.code) {
          const getApi = apiPaths.find(p => p.method === 'GET');
          if (getApi) {
            const codeMatch = getApi.path.match(/^\/(\w+)/);
            if (codeMatch) model.code = codeMatch[1];
          }
        }
      }

      if (type === 'ENUM') {
        // @EnumDict
        const ecMatch = content.match(/@EnumDict\s*\(\s*enumCode\s*=\s*"([^"]+)"/);
        if (ecMatch) model.enumCode = ecMatch[1];
        const elMatch = content.match(/@EnumDict[\s\S]*?enumLabel\s*=\s*"([^"]+)"/);
        if (elMatch) model.enumLabel = elMatch[1];

        model.code = model.enumCode || simpleName.replace('Enum', '');

        // 提取枚举常量
        const constants = this.extractEnumConstants(content);
        model.constantsJson = JSON.stringify(constants);
      }

      if (type === 'SERVICE' || type === 'SERVICE_IMPL') {
        // 提取接口/类名作为code
        model.code = simpleName.replace('Service', '').replace('Impl', '');
      }

      if (type === 'MAPPER') {
        model.code = simpleName.replace('Mapper', '');
      }

      if (type === 'VO' || type === 'DTO') {
        const fields = this.extractEntityFields(content, type);
        model.fieldsJson = JSON.stringify(fields);
      }

      return model;
    },

    extractEntityFields(content, contextType) {
      const fields = [];
      // 找到类体开始（第一个 { ）
      const bodyStart = content.indexOf('{');
      if (bodyStart === -1) return fields;

      const body = content.substring(bodyStart);

      // 匹配字段定义：private/protected/public Type name;
      const fieldRegex = /(?:private|protected|public)\s+([\w.]+(?:<[\w. ,]+>)?)\s+(\w+)\s*;/g;
      let match;
      while ((match = fieldRegex.exec(body)) !== null) {
        const typeStr = match[1].replace(/<[\s\S]*>/, '').trim();
        const fieldName = match[2];
        // 跳过 serialVersionUID
        if (fieldName === 'serialVersionUID') continue;
        // 提取字段注释
        const prevText = body.substring(Math.max(0, match.index - 200), match.index);
        const commentMatch = prevText.match(/\/\*\*[\s\S]*?\*\/\s*$/) ||
            prevText.match(/\/\/\s*(.+)$/m);
        let fieldDesc = '';
        if (commentMatch) {
          const lines = commentMatch[0].split('\n');
          for (const line of lines) {
            const cleaned = line.replace(/^\s*\*\s?/, '').replace(/\/\//, '').trim();
            if (cleaned && !cleaned.startsWith('@')) {
              fieldDesc = cleaned;
              break;
            }
          }
        }
        // 提取 @ApiModelProperty value
        const apiPropMatch = prevText.match(/@ApiModelProperty\s*\(\s*(?:value\s*=\s*)?"([^"]+)"/);
        if (apiPropMatch) fieldDesc = apiPropMatch[1];

        fields.push({
          name: fieldName,
          type: typeStr,
          description: fieldDesc || ''
        });
      }
      return fields;
    },

    extractControllerApis(content) {
      const apis = [];
      // 提取类级别的 @RequestMapping
      const classMapping = content.match(/@RequestMapping\s*\(\s*(?:value\s*=\s*)?"([^"]+)"/);
      const basePath = classMapping ? classMapping[1] : '';

      // 匹配所有 @XxxMapping
      const apiRegex = /@(GetMapping|PostMapping|PutMapping|DeleteMapping)\s*\(\s*(?:value\s*=\s*)?"([^"]+)"/g;
      let match;
      while ((match = apiRegex.exec(content)) !== null) {
        const method = match[1].replace('Mapping', '').toUpperCase();
        let path = match[2];
        if (basePath) {
          path = path.startsWith('/') ? basePath + path : basePath + '/' + path;
        }
        if (!path.startsWith('/')) {
          path = '/' + path;
        }
        apis.push({ method, path });
      }
      return apis;
    },

    extractEnumConstants(content) {
      const constants = [];
      // 找到 enum 体开始
      const enumBodyStart = content.indexOf('{', content.indexOf('enum'));
      if (enumBodyStart === -1) return constants;

      const body = content.substring(enumBodyStart + 1);

      // 匹配常量行：NAME(value, "desc") 或 NAME, 或 NAME(value)
      // 先提取常量定义块（分号之前的部分）
      const semiIdx = body.indexOf(';');
      const constBlock = semiIdx !== -1 ? body.substring(0, semiIdx) : body;

      // 按行分割，每行是一个或一组常量
      const lines = constBlock.split('\n');
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed === ';' || trimmed.startsWith('//') || trimmed.startsWith('/*') || trimmed.startsWith('*')) continue;

        // 匹配 NAME(value, "desc") 或 NAME("value", "desc") 或 NAME
        const parts = trimmed.replace(/,$/, '').split('(');
        const constName = parts[0].trim();
        if (!constName || constName === '') continue;

        let constValue = constName;
        let constDesc = '';

        if (parts.length > 1) {
          // 有参数：提取 value 和 desc
          const argsStr = parts[1].replace(/\)[\s,]*$/, '');
          // 获取行注释作为描述
          const commentMatch = trimmed.match(/\/\/\s*(.+)$/);
          if (commentMatch) constDesc = commentMatch[1].trim();

          // 尝试提取第一个参数作为值
          const args = argsStr.split(',');
          if (args.length >= 1) {
            const firstArg = args[0].trim().replace(/"/g, '');
            if (firstArg) constValue = firstArg;
          }
        }

        constants.push({
          name: constName,
          value: constValue,
          desc: constDesc || constName
        });
      }
      return constants;
    },

    async submitProjectModel() {
      if (!this.queryParams.projectId || this.javaModels.length === 0) return;
      try {
        // 给每个模型补上 projectId，直接传 list
        const items = this.javaModels.map(m => ({
          ...m,
          projectId: this.queryParams.projectId
        }));
        await batchSaveProjectModel(items);
        this.modelScanDone = true;
        this.modelScanCount = this.javaModels.length;
      } catch (error) {
        console.error('保存扫描结果失败:', error);
        throw error;
      }
    },

    /* ========== 向导步骤4：扫描入口 ========== */
    async loadExistingScanData() {
      // 从DB加载已有扫描数据（无content，仅展示统计）
      try {
        const res = await getProjectModelList(this.queryParams.projectId);
        const list = res.data || [];
        if (list.length > 0) {
          this.javaModels = list;
          this.recalcScanSummary();
          this.modelScanDone = true;
          this.modelScanCount = list.length;
          this.detectProjectPaths();
          this.$message.info(`加载已有扫描数据：${list.length} 个文件`);
        } else if (this.directoryHandle) {
          await this.startJavaScan();
        }
      } catch (e) {
        console.error('加载已有扫描数据失败:', e);
        if (this.directoryHandle) {
          await this.startJavaScan();
        }
      }
    },

    async loadScanDataSilent() {
      // 工作台视图静默加载已有扫描数据（不自动开始扫描）
      if (!this.queryParams.projectId) return;
      try {
        const res = await getProjectModelList(this.queryParams.projectId);
        const list = res.data || [];
        if (list.length > 0) {
          this.javaModels = list;
          this.recalcScanSummary();
          this.modelScanDone = true;
          this.modelScanCount = list.length;
          this.detectProjectPaths();
        }
      } catch (e) {
        // 静默失败
      }
    },

    /* ========== 开始扫描（步骤4调用） ========== */
    async startJavaScan() {
      if (!this.directoryHandle) {
        this.$message.warning('请先选择项目目录');
        return;
      }
      // 对非 Java 项目使用 AI 驱动扫描
      if (this.projectTypeKey !== 'MAVEN' && this.projectTypeKey !== 'GRADLE' && this.projectTypeKey) {
        await this.scanNonJavaSources();
        return;
      }
      if (this.scanMode === 'manual') {
        await this.scanJavaSourcesManual();
      } else {
        await this.scanJavaSourcesPipeline();
      }
    },

    /* ========== 重新扫描（已扫描后调用） ========== */
    async rescanJavaSources() {
      if (!this.directoryHandle) {
        this.$message.warning('请先选择项目目录');
        return;
      }
      // 非 Java 项目直接重新扫描
      if (this.projectTypeKey !== 'MAVEN' && this.projectTypeKey !== 'GRADLE' && this.projectTypeKey) {
        await this.scanNonJavaSources();
        return;
      }
      await this.loadProjectStrategy();
      this.pendingStartScan = true;
      this.forceRefreshScan = true;

      const hasConfig = this.strategyConfig.aiDisclosure || this.strategyConfig.scanScriptId;
      if (hasConfig) {
        this.onScanStrategyConfirm(this.strategyConfig);
      } else {
        this.scanStrategyDialogVisible = true;
      }
    },

    /* ========== 从扫描结果自动检测模块路径 ========== */
    detectProjectPaths() {
      const detected = {};
      for (const m of this.javaModels) {
        if (!m.moduleName || !m.filePath) continue;
        if (!detected[m.moduleName]) {
          detected[m.moduleName] = { javaPath: '', vuePath: '' };
        }
        // 检测 javaPath：通过 className 反推包路径并移除
        if (!detected[m.moduleName].javaPath && m.className) {
          const lastDot = m.className.lastIndexOf('.');
          if (lastDot > 0) {
            const pkgName = m.className.substring(0, lastDot);
            const pkgPath = pkgName.replace(/\./g, '/');
            const fileDir = m.filePath.substring(0, m.filePath.lastIndexOf('/'));
            if (fileDir.endsWith(pkgPath)) {
              const base = fileDir.substring(0, fileDir.length - pkgPath.length);
              detected[m.moduleName].javaPath = base.replace(/\/$/, '');
            }
          }
        }
      }
      // 先同步设置 javaPath
      this.detectedModulePaths = detected;
      // 再异步检测 vuePath 并补充
      this.detectVuePath().then(vuePath => {
        if (vuePath) {
          const updated = { ...this.detectedModulePaths };
          Object.keys(updated).forEach(k => {
            if (!updated[k].vuePath) updated[k].vuePath = vuePath;
          });
          this.detectedModulePaths = updated;
        }
      });
    },

    async detectVuePath() {
      if (!this.directoryHandle) return '';
      try {
        // 在项目根目录下查找常见的 Vue 工程目录
        for (const suffix of ['-vue', '-admin', '-frontend', '-web', '-ui']) {
          for await (const [name, handle] of this.directoryHandle.entries()) {
            if (handle.kind === 'directory' && name.endsWith(suffix)) {
              // 检查是否存在 src/views
              for await (const [subName, subHandle] of handle.entries()) {
                if (subHandle.kind === 'directory' && subName === 'src') {
                  for await (const [srcName] of subHandle.entries()) {
                    if (srcName === 'views') {
                      return name + '/src/views';
                    }
                  }
                }
              }
            }
          }
        }
      } catch (e) {
        console.warn('自动检测 Vue 路径失败:', e);
      }
      return '';
    },

    /* ========== 完成扫描，进入工作台 ========== */
    finishScan() {
      this.wizardActive = false;
    },

    recalcScanSummary() {
      const s = { total: this.javaModels.length, enums: 0, entities: 0, controllers: 0, services: 0, others: 0 };
      for (const m of this.javaModels) {
        if (m.type === 'ENUM') s.enums++;
        else if (m.type === 'ENTITY') s.entities++;
        else if (m.type === 'CONTROLLER') s.controllers++;
        else if (m.type === 'SERVICE' || m.type === 'SERVICE_IMPL') s.services++;
        else s.others++;
      }
      this.scanSummary = s;
    },

    /* ========== AI 批量分类 ========== */
    async classifyByAI(files, disclosure = 'COMPACT') {
      const config = this.$ai || {};
      const endpoint = config.endpoint || '/v1/chat/completions';
      const model = config.model || 'ernie-4.0';
      const apiKey = config.apiKey || '';

      // 根据披露策略构建输入内容量
      const inputs = files.map(f => {
        const summary = this.buildFileSummary(f);
        if (disclosure === 'FULL') {
          // 全文模式：摘要 + 文件内容
          const content = f._originalContent || f.content || '';
          return summary + '\n--- file content (前100行) ---\n' + content.split('\n').slice(0, 100).join('\n');
        }
        // COMPACT / PROGRESSIVE 第一轮：仅摘要
        return summary;
      });

      const headers = { 'Content-Type': 'application/json' };
      if (apiKey) headers['Authorization'] = 'Bearer ' + apiKey;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        signal: AbortSignal.timeout(30000),
        body: JSON.stringify({
          model,
          messages: [
            { role: 'system', content: this.aiSystemPrompt() },
            { role: 'user', content: '请分类以下文件（只返回JSON数组）：\n' + inputs.join('\n') }
          ],
          stream: false
        })
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const result = await res.json();
      const text = result.data || result.choices?.[0]?.message?.content || '';
      const cleaned = text.replace(/```(?:json)?\s*([\s\S]*?)\s*```/g, '$1').trim();
      try {
        return JSON.parse(cleaned);
      } catch {
        console.warn('AI返回格式异常:', text);
        return [];
      }
    },

    buildFileSummary(file) {
      const content = file.content || '';
      const annotations = (content.match(/@\w+/g) || [])
        .filter(a => !['@Override', '@SuppressWarnings', '@Deprecated'].includes(a))
        .slice(0, 8).join(',');
      const pkg = (content.match(/package\s+([\w.]+);/) || [])[1] || '';
      const isEnum = content.includes(' enum ');
      const isInterface = !isEnum && content.includes(' interface ');
      const extMatch = content.match(/extends\s+(\w+)/);
      const implMatch = content.match(/implements\s+([\w,\s]+)/);
      return `fileName:${file.fileName}|pkg:${pkg}|ann:${annotations}|ext:${extMatch ? extMatch[1] : '-'}|impl:${implMatch ? implMatch[1].replace(/,\s*/g, ',') : '-'}|enum:${isEnum}|inf:${isInterface}`;
    },

    aiSystemPrompt() {
      return '你是一个Java源码分类专家。将Java源文件分类到以下类型之一：\n' +
        'ENUM(枚举), ENTITY(实体,含@TableName), VO(视图对象), DTO(数据传输对象), ' +
        'CONTROLLER(控制器), SERVICE(服务接口), SERVICE_IMPL(服务实现), MAPPER(映射器), ' +
        'CONFIG(配置类), INTERFACE(接口/抽象类), OTHER(其他)\n' +
        '规则：文件名后缀和包路径是关键信号。只返回JSON：[{"fileName":"","type":""}]';
    },

    /* ========== 人工标注弹窗 ========== */
    openScanLabelDialog() {
      if (!this.hasScannedFiles) {
        this.$message.info('暂无已扫描的文件，请先执行扫描');
        return;
      }
      // 优先用 javaModels（含机械提取），否则用 scanLabelFiles（原始收集）
      const files = this.javaModels.length > 0 ? this.javaModels : this.scanLabelFiles;
      files.forEach(f => { f._checked = f._checked !== undefined ? f._checked : false; });
      this.scanLabelFiles = files;
      this.scanLabelDialogVisible = true;
    },

    async onScanLabelConfirm(files) {
      let newModels = false;
      for (const f of files) {
        if (!f.type || f.type === 'OTHER') continue;
        // 检查是否已存在
        const existing = this.javaModels.find(m => m.filePath === f.filePath);
        if (!existing) {
          // 来自仅收集模式的原始文件，需要做机械提取
          const model = this.parseJavaFileContent(f.fileName, f.filePath, f.content);
          if (model) {
            model.type = f.type; // 用户选择的类型优先
            model._classifiedBy = 'manual';
            this.javaModels.push(model);
            newModels = true;
          }
        } else if (existing.type !== f.type) {
          existing.type = f.type;
          existing._classifiedBy = 'manual';
        }
      }
      if (newModels || this.javaModels.length > 0) {
        this.modelScanDone = false;
        await this.submitProjectModel();
      }
      this.$message.success('标注已保存');
    },

    /* ========== 生成代码按钮 ========== */
    async handleGenerateCode(module) {
      if (!module.configId) {
        this.$message.warning('请先配置数据源（作者名、包名为必填）');
        return;
      }
      // 验证模块配置的必填字段
      try {
        const res = await getModuleWithConfig(module.id);
        const m = res.data;
        if (!m || !m.authorName) {
          this.$message.warning('数据源配置缺少作者名，请点击「配置数据源」填写');
          return;
        }
        if (!m.packageName) {
          this.$message.warning('数据源配置缺少包名，请点击「配置数据源」填写');
          return;
        }
      } catch (e) {
        console.error('验证模块配置失败:', e);
      }
      this.moduleActionDialogVisible = false;
      this.wizardModuleId = module.id;
      this.wizardModuleName = module.name;
      this.wizardConfigId = module.configId;
      this.wizardPrefix = module.prefix || '';
      this.wizardVisible = true;
    },

    openScanSettings() {
      // 打开扫描设置弹窗（不自动触发扫描）
      this.loadProjectStrategy();
      this.pendingStartScan = false;
      this.scanStrategyDialogVisible = true;
    },

    showModuleScanResults() {
      // 从动作弹窗查看该模块的扫描结果
      if (!this.activeModule) return;
      const moduleFiles = this.javaModels.filter(m => {
        if (!this.activeModule.name) return false;
        return m.moduleName === this.activeModule.name || m.filePath.startsWith(this.activeModule.name + '/');
      });
      this.selectedModuleForScan = this.activeModule;
      this.selectedModuleFiles = moduleFiles;
      this.moduleActionDialogVisible = false;
      this.moduleScanDialogVisible = true;
    },

    /* ========== 扫描策略配置 ========== */

    async loadProjectStrategy() {
      // 从后端加载项目当前的扫描策略配置
      if (!this.queryParams.projectId) return;
      try {
        const res = await getProject(this.queryParams.projectId);
        const project = res.data;
        if (project) {
          const config = { strategyType: 'default', aiDisclosure: 'COMPACT', scanScriptId: null };
          if (project.scanScriptId) {
            config.strategyType = 'custom';
            config.scanScriptId = project.scanScriptId;
          }
          if (project.aiDisclosure) {
            config.aiDisclosure = project.aiDisclosure;
          }
          this.strategyConfig = config;
        }
      } catch (e) {
        console.error('加载项目策略失败:', e);
      }
    },

    onStrategyDialogVisibleChange(val) {
      this.scanStrategyDialogVisible = val;
      if (!val) {
        // 对话框关闭（未确认或跳过），取消待开始的扫描
        this.pendingStartScan = false;
        this.forceRefreshScan = false;
      }
    },

    async onScanStrategyConfirm(config) {
      this.strategyConfig = config;
      // 保存自定义脚本引用
      if (config.strategyType === 'custom' && config.scanScriptId) {
        this.usingCustomScript = true;
        try {
          const res = await getScanScriptList();
          const scripts = res.data || [];
          const script = scripts.find(s => s.id === config.scanScriptId);
          this.customScriptContent = script ? script.content : '';
        } catch (e) {
          console.error('加载脚本内容失败:', e);
          this.customScriptContent = '';
        }
      } else {
        this.usingCustomScript = false;
        this.customScriptContent = '';
      }
      // 开始扫描
      if (this.pendingStartScan) {
        this.pendingStartScan = false;
        if (this.forceRefreshScan) {
          // 重新扫描：直接开始新的扫描（覆盖已有数据）
          this.forceRefreshScan = false;
          await this.startJavaScan();
        } else {
          // 首次进入：检查已有扫描数据，无则开始扫描
          await this.loadExistingScanData();
        }
      }
    },

    onScanStrategySkip() {
      this.pendingStartScan = false;
      this.forceRefreshScan = false;
      this.$message.info('已跳过扫描');
    },

    openScriptGenerator(script) {
      this.editingScript = script || null;
      this.scriptGeneratorDialogVisible = true;
    },

    onScriptSaved(script) {
      this.$message.success('脚本已保存');
      this.$nextTick(() => {
        this.$refs.strategyDialog?.loadScriptList();
      });
    },

    /* ========== 扫描结果标签辅助 ========== */
    scanTagType(row) {
      if (!row.type || row.type === 'OTHER') return 'info';
      if (row.type === 'ENUM') return 'warning';
      if (row.type === 'ENTITY') return 'primary';
      if (row.type === 'CONTROLLER') return 'success';
      return '';
    },

    classifiedByLabel(val) {
      const map = { 'rule': '规则', 'ai': 'AI', 'manual': '人工', 'RULE': '规则', 'AI': 'AI', 'MANUAL': '人工', 'CUSTOM_SCRIPT': '脚本' };
      return map[val] || val || '-';
    },

  }
}
</script>

<style lang="less" scoped>
  .workspace-container {
    height: 100%;
    overflow-y: auto;
  }

  .workspace-layout {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    padding: 20px;
    min-height: 100%;
    background: #F2F5F9;
  }

  .workspace-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .workspace-sidebar {
    width: 260px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /* ==============================
     卡片基础
  ============================== */
  .card {
    background: #fff;
    border-radius: 8px;
    border: 1px solid #E4E7ED;
    padding: 20px;
  }
  .card-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    gap: 8px;
    h3 {
      font-size: 15px;
      font-weight: 600;
      color: #1a1a2e;
      flex: 1;
      margin: 0;
      i {
        margin-right: 6px;
        color: #409EFF;
      }
    }
    .card-header-right {
      margin-left: auto;
    }
  }
  .card-body {
    .card-desc {
      font-size: 13px;
      color: #909399;
      margin-bottom: 16px;
    }
  }
  .card-footer {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #F0F2F5;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  .step-card {
    padding: 16px 20px;
  }

  /* ==============================
     项目选择区
  ============================== */
  .project-select-row {
    display: flex;
    gap: 12px;
    align-items: center;
  }
  .project-select {
    flex: 1;
    max-width: 320px;
  }

  /* ==============================
     路径选择区
  ============================== */
  .info-banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 16px;
    background: #FFF8E6;
    border: 1px solid #FFE58F;
    border-radius: 6px;
    font-size: 13px;
    color: #876800;
    line-height: 1.6;
    margin-bottom: 14px;
    i {
      font-size: 16px;
      flex-shrink: 0;
      margin-top: 1px;
    }
    em {
      font-style: normal;
      font-weight: 600;
    }
  }
  .path-row {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 8px;
  }
  .path-input {
    flex: 1;
  }
  .tip-text {
    font-size: 12px;
    color: #909399;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .scanning-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    gap: 12px;
    .scanning-spin {
      font-size: 36px;
      color: #409EFF;
      animation: spin 1s linear infinite;
    }
    p {
      font-size: 13px;
      color: #909399;
      margin: 0;
    }
  }

  /* ==============================
     扫描结果区
  ============================== */
  .scan-result-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    h4 {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      margin: 0;
    }
    span {
      font-size: 12px;
      color: #909399;
    }
  }

  .module-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 400px;
    overflow-y: auto;
  }
  .module-item {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    border: 1px solid #E4E7ED;
    border-radius: 6px;
    gap: 12px;
    cursor: pointer;
    transition: all .15s;
    background: #fff;
    margin-bottom: 0;
    &:hover {
      border-color: #409EFF;
      background: #F5F9FF;
    }
    &.module-item-checked {
      border-color: #409EFF;
      background: #ECF5FF;
    }
    &.module-item-existing {
      border-color: #B7EB8F;
      background: #F6FFED;
      cursor: default;
      &:hover {
        border-color: #B7EB8F;
        background: #F6FFED;
      }
    }
    &.module-item-orphan {
      border-color: #E4E7ED;
      background: #FAFAFA;
      cursor: default;
      opacity: 0.7;
      &:hover {
        border-color: #E4E7ED;
        background: #FAFAFA;
      }
      .module-item-name {
        color: #909399;
      }
    }
  }
  .module-checkbox {
    width: 16px;
    height: 16px;
    border: 1.5px solid #DCDFE6;
    border-radius: 3px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    font-size: 11px;
    &.module-checkbox-checked {
      background: #409EFF;
      border-color: #409EFF;
      color: #fff;
    }
    &.module-checkbox-disabled {
      background: #F5F5F5;
      border-color: #D9D9D9;
      cursor: not-allowed;
      &.module-checkbox-checked {
        background: #A0CFFF;
        border-color: #A0CFFF;
        color: #fff;
      }
    }
  }
  .module-item-existing .module-checkbox-checked {
    background: #67C23A;
    border-color: #67C23A;
  }
  .module-info {
    flex: 1;
    min-width: 0;
  }
  .module-item-name {
    font-size: 13px;
    font-weight: 500;
    color: #1a1a2e;
  }
  .module-item-path {
    font-size: 11px;
    color: #909399;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .missing-badge {
    display: inline-block;
    font-size: 10px;
    padding: 1px 6px;
    border-radius: 8px;
    background: #FFF1F0;
    color: #FF4D4F;
    margin-left: 8px;
    font-weight: 400;
    vertical-align: middle;
  }
  .module-tag {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 10px;
    white-space: nowrap;
    &.tag-api {
      background: #FDF6EC;
      color: #E6A23C;
    }
    &.tag-web {
      background: #ECF5FF;
      color: #409EFF;
    }
    &.tag-service {
      background: #F0F9EB;
      color: #67C23A;
    }
    &.tag-common {
      background: #F4F4F5;
      color: #909399;
    }
    &.tag-existing {
      background: #F0F9EB;
      color: #67C23A;
    }
    &.tag-orphan {
      background: #F5F5F5;
      color: #C0C4CC;
    }
  }

  .check-bar {
    display: flex;
    align-items: center;
    padding: 10px 16px;
    background: #ECF5FF;
    border: 1px solid #b3d8ff;
    border-radius: 6px;
    gap: 12px;
    margin-top: 12px;
  }
  .check-count {
    font-size: 13px;
    color: #409EFF;
    font-weight: 500;
  }
  .check-actions {
    margin-left: auto;
    display: flex;
    gap: 8px;
  }

  /* ==============================
     状态 B：项目信息条
  ============================== */
  .project-info-bar {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    background: #ECF5FF;
    border: 1px solid #b3d8ff;
    border-radius: 8px;
  }
  .project-info-content {
    flex: 1;
  }
  .project-info-name {
    font-size: 15px;
    font-weight: 600;
    color: #409EFF;
    i {
      margin-right: 6px;
    }
  }
  .project-info-meta {
    font-size: 12px;
    color: #606266;
    display: flex;
    gap: 16px;
    margin-top: 4px;
    span {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
  .change-project-btn {
    background: none;
    border: 1px solid #b3d8ff;
    color: #409EFF;
    border-radius: 5px;
    padding: 4px 12px;
    cursor: pointer;
    font-size: 12px;
    &:hover {
      background: #fff;
    }
  }

  /* ==============================
     模块卡片网格
  ============================== */
  .module-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 12px;
  }
  .module-card {
    background: #fff;
    border: 1px solid #E4E7ED;
    border-radius: 8px;
    padding: 16px;
    cursor: pointer;
    transition: all .2s;
    position: relative;
    overflow: hidden;
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: #409EFF;
    }
    &:hover {
      border-color: #409EFF;
      box-shadow: 0 4px 12px rgba(64,158,255,.12);
      transform: translateY(-2px);
    }
    &.module-card-green::before { background: #67C23A; }
    &.module-card-orange::before { background: #E6A23C; }
    &.module-card-purple::before { background: #9c27b0; }
  }
  .module-card-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #ECF5FF;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-bottom: 12px;
    color: #409EFF;
  }
  .module-card-green .module-card-icon { background: #F0F9EB; color: #67C23A; }
  .module-card-orange .module-card-icon { background: #FDF6EC; color: #E6A23C; }
  .module-card-purple .module-card-icon { background: #F3E5F5; color: #9c27b0; }

  .module-card-name {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a2e;
    margin-bottom: 4px;
    word-break: break-all;
  }
  .module-card-desc {
    font-size: 12px;
    color: #909399;
    line-height: 1.5;
    margin-bottom: 10px;
  }
  .module-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .module-card-del {
    position: absolute;
    bottom: 8px;
    right: 8px;
    font-size: 16px;
    color: #C0C4CC;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    opacity: 0;
    transition: all .2s;
    z-index: 1;
    &:hover {
      color: #F56C6C;
      background: #FEF0F0;
    }
  }
  .module-card-gencode {
    position: absolute;
    top: 6px;
    right: 32px;
    font-size: 16px;
    color: #C0C4CC;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    opacity: 0;
    transition: all .2s;
    z-index: 1;
    &:hover {
      color: #E6A23C;
      background: #FDF6EC;
    }
  }
  .module-card-config {
    position: absolute;
    top: 6px;
    right: 8px;
    font-size: 16px;
    color: #C0C4CC;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    opacity: 0;
    transition: all .2s;
    z-index: 1;
    &:hover {
      color: #409EFF;
      background: #ECF5FF;
    }
  }
  .module-card:hover .module-card-del,
  .module-card:hover .module-card-config,
  .module-card:hover .module-card-gencode {
    opacity: 1;
  }
  .module-card-tag {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 10px;
    &.tag-api { background: #FDF6EC; color: #E6A23C; }
    &.tag-web { background: #ECF5FF; color: #409EFF; }
    &.tag-service { background: #F0F9EB; color: #67C23A; }
    &.tag-common { background: #F4F4F5; color: #909399; }
  }

  /* ==============================
     空状态
  ============================== */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    gap: 12px;
    .empty-icon {
      font-size: 48px;
      color: #C0C4CC;
    }
    p {
      font-size: 14px;
      color: #909399;
      margin: 0;
    }
    small {
      font-size: 12px;
      color: #C0C4CC;
    }
  }

  /* ==============================
     右侧栏
  ============================== */
  .sidebar-card {
    background: #fff;
    border: 1px solid #E4E7ED;
    border-radius: 8px;
    padding: 16px;
    h4 {
      font-size: 13px;
      font-weight: 600;
      color: #333;
      margin: 0 0 12px 0;
      padding-bottom: 8px;
      border-bottom: 1px solid #F0F2F5;
      i {
        margin-right: 6px;
      }
    }
  }
  .action-btn-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .action-btn {
    width: 100%;
    margin: 0 !important;
    margin-left: 0 !important;
  }

  .stat-row {
    display: flex;
    gap: 8px;
  }
  .stat-item {
    flex: 1;
    text-align: center;
    padding: 10px 8px;
    background: #F5F7FA;
    border-radius: 6px;
  }
  .stat-num {
    font-size: 20px;
    font-weight: 700;
    color: #409EFF;
  }
  .stat-label {
    font-size: 11px;
    color: #909399;
    margin-top: 2px;
  }

  .usage-guide {
    font-size: 12px;
    color: #606266;
    line-height: 2;
    p {
      margin: 0;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .step-num {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #ECF5FF;
      color: #409EFF;
      font-size: 11px;
      font-weight: 600;
      flex-shrink: 0;
    }
    .usage-note {
      margin-top: 8px;
      color: #909399;
    }
  }

  .ai-assistant-card {
    padding: 0 !important;
    overflow: hidden;
    height: 420px;
    display: flex;
    flex-direction: column;
  }

  .ai-assistant-card :deep(.sidebar-card) {
    height: 100%;
    margin: 0;
    padding: 0;
    border: none;
  }
</style>
